// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// Nine routes for three kinds of person: whoever asks "are we all right" lands on
// /sop and reads the executive summary; a planner lives on /sop/plans and inside
// one plan, whose nine views are tabs rather than routes because they are nine
// views of the same document; and whoever keeps the master data works on
// /sop/items and /sop/work-centers.
//
// The plan detail is one route with a ?tab= query and not eleven routes, so a
// link to "the capacity of SOP-2027" is a link somebody can paste into a
// message — which is most of what an S&OP meeting does.

export const routes = [
  {
    path: '/sop',
    name: 'sop-cockpit',
    component: () => import('./pages/CockpitPage.vue'),
    meta: { permission: 'sop:read', title: 'sop.cockpit.title' },
  },
  {
    path: '/sop/plans',
    name: 'sop-plans',
    component: () => import('./pages/PlansPage.vue'),
    meta: { permission: 'sop:read', title: 'sop.plans.title' },
  },
  // The static path wins over /:id by route ranking, so the order here does not
  // matter — but it sits first anyway, for the reader.
  {
    path: '/sop/plans/new',
    name: 'sop-plan-new',
    component: () => import('./pages/PlanFormPage.vue'),
    meta: { permission: 'sop:admin', title: 'sop.plans.newTitle' },
  },
  {
    path: '/sop/plans/:id',
    name: 'sop-plan',
    component: () => import('./pages/PlanPage.vue'),
    meta: { permission: 'sop:read', title: 'sop.plans.detailTitle' , ownsQuery: true /* la pestaña abierta va en la consulta: el armazón no la remonta al cambiar */ },
  },
  {
    path: '/sop/items',
    name: 'sop-items',
    component: () => import('./pages/ItemsPage.vue'),
    meta: { permission: 'sop:read', title: 'sop.items.title' },
  },
  {
    path: '/sop/items/new',
    name: 'sop-item-new',
    component: () => import('./pages/ItemFormPage.vue'),
    meta: { permission: 'sop:supply', title: 'sop.items.new' },
  },
  {
    path: '/sop/items/:id',
    name: 'sop-item',
    component: () => import('./pages/ItemFormPage.vue'),
    meta: { permission: 'sop:read', title: 'sop.items.edit' },
  },
  {
    path: '/sop/work-centers',
    name: 'sop-work-centers',
    component: () => import('./pages/WorkCentersPage.vue'),
    meta: { permission: 'sop:read', title: 'sop.centers.title' },
  },
]

/**
 * One row that opens, and not four — the same reasoning as inventory and
 * production.
 *
 * The parent points at the SUMMARY, which is the screen everybody with
 * 'sop:read' can open and the one that answers the question somebody arrives
 * with. The master data hangs underneath because it is where a planner goes
 * once a quarter, not once a morning.
 */
export const navigation = [
  {
    to: '/sop',
    labelKey: 'sop.title',
    permission: 'sop:read',
    icon: 'forecast',
    group: 'business',
    children: [
      { to: '/sop', labelKey: 'sop.cockpit.title', permission: 'sop:read' },
      { to: '/sop/plans', labelKey: 'sop.plans.title', permission: 'sop:read' },
      { to: '/sop/items', labelKey: 'sop.items.title', permission: 'sop:read' },
      { to: '/sop/work-centers', labelKey: 'sop.centers.title', permission: 'sop:read' },
    ],
  },
]

// Strings this module contributes, merged per locale. Four languages from the
// first commit (§16): a module that ships two is a module that cannot be sold to
// the third client. That includes what this module is called on the PLATFORM's
// own screens — Roles, Settings, Audit, the home dashboard and the assistant
// panel — because a module that makes somebody add keys to the shell is not
// extractable (§4.2).
//
// The four blocks below are generated from one table so they cannot drift apart:
// the shell's i18n test requires them to hold exactly the same set of keys.
export const messages = {
  es: {
    sop: {
      title: "Planificación S&OP",
      search: {
        subtitle: "Plan táctico",
      },
      notifications: {
        categories: {
          planning: "Planificación S&OP",
        },
      },
      units: {
        days: "{count} días",
      },
      actions: {
        newPlan: "Nuevo plan",
        createPlan: "Crear el plan",
        calculate: "Calcular",
        approve: "Aprobar",
        handTo: "Pasar a {stage}",
        save: "Guardar",
        cancel: "Cancelar",
        apply: "Aplicar",
        remove: "Quitar",
        archive: "Archivar",
        openCatalogue: "Ir al catálogo",
        openWorkCenters: "Ir a centros de trabajo",
        seedDemo: "Crear datos de ejemplo",
      },
      pager: {
        previous: "Anterior",
        next: "Siguiente",
        of: "Página {page} de {pages}",
      },
      empty: {
        none: "Todavía no hay ningún plan S&OP",
        noneHint: "Crea el primero para analizar demanda, capacidad e inventario de los próximos meses.",
        filtered: "Sin resultados",
        filteredHint: "Prueba a quitar algún filtro o a buscar otra cosa.",
      },
      status: {
        draft: "Borrador",
        preparation: "Preparación",
        demand_review: "Revisión de demanda",
        supply_review: "Revisión de suministro",
        capacity_review: "Revisión de capacidad",
        scenario_review: "Revisión de escenarios",
        financial_review: "Revisión financiera",
        executive_review: "Reunión de dirección",
        approved: "Aprobado",
        closed: "Cerrado",
      },
      cycle: {
        title: "Ciclo S&OP",
        moved: "El plan pasa a {stage}",
      },
      calc: {
        never: "Sin calcular",
        stale: "Desactualizado",
        running: "Calculando",
        ready: "Actualizado",
        failed: "Error de cálculo",
        lastRun: "Último cálculo: {when}",
        done: "Plan calculado en {ms} ms · {risks} riesgos",
        notice: {
          never: "Este plan todavía no se ha calculado: las pantallas están vacías hasta que lo hagas.",
          stale: "Algo ha cambiado desde el último cálculo. Las cifras que ves son las de antes.",
          running: "El plan se está calculando.",
          ready: "Las cifras están al día.",
          failed: "El último cálculo falló. Revisa la preparación de datos y vuelve a intentarlo.",
        },
      },
      granularity: {
        month: "Mes",
        week: "Semana",
        quarter: "Trimestre",
      },
      fence: {
        frozen: "congelado",
        firm: "firme",
        flexible: "flexible",
      },
      plans: {
        title: "Planes",
        subtitle: "Los planes S&OP de la empresa, y en qué punto del ciclo está cada uno",
        detailTitle: "Plan S&OP",
        newTitle: "Nuevo plan S&OP",
        newSubtitle: "Qué se planifica, hasta cuándo y con qué límites",
        versionLabel: "v{version}",
        created: "Plan {code} creado",
      },
      table: {
        code: "Código",
        name: "Plan",
        horizon: "Horizonte",
        status: "Estado",
        calc: "Cálculo",
        version: "Versión",
      },
      tabs: {
        overview: "Resumen",
        demand: "Demanda",
        supply: "Suministro",
        capacity: "Capacidad",
        inventory: "Inventario",
        procurement: "Compras",
        scenarios: "Escenarios",
        risks: "Riesgos",
        versions: "Plan aprobado",
        actual: "Seguimiento",
      },
      filters: {
        search: "Buscar",
        searchPlaceholder: "Código o nombre del plan…",
        status: "Estado",
        allStatuses: "Todos",
        family: "Familia",
        allFamilies: "Todas las familias",
        sourcing: "Origen",
        allSourcings: "Fabricado y comprado",
        planned: "En el plan",
        allItems: "Todo el catálogo",
        state: "Situación",
        allStates: "Todas las situaciones",
        risk: "Riesgo",
        allRisks: "Todos",
        severity: "Gravedad",
        allSeverities: "Todas",
        riskType: "Tipo",
        allTypes: "Todos los tipos",
      },
      kpi: {
        openPlans: "Planes abiertos",
        approvedPlans: "Planes aprobados",
        closedPlans: "Planes cerrados",
        needsCalc: "Pendientes de calcular",
        demand: "Demanda prevista",
        confirmed: "Ya confirmado",
        peakUtilisation: "Utilización máxima",
        capacityGap: "Horas que faltan",
        stockouts: "Materiales con rotura",
        service: "Nivel de servicio estimado",
        purchaseRisk: "Compras en riesgo",
        revenue: "Facturación prevista",
        cost: "Coste previsto",
        margin: "Margen previsto",
        inventoryValue: "Inventario máximo",
      },
      cockpit: {
        title: "Resumen S&OP",
        subtitle: "Cómo va el plan y qué hay que decidir",
        pick: "Plan",
        openPlan: "Abrir el plan",
        decisions: "Decisiones que hay que tomar",
        decisionsHint: "Lo ya escrito primero, y después los riesgos críticos que nadie ha decidido todavía",
        noDecisions: "Nada pendiente de decidir",
        criticalRisks: "Riesgos críticos",
        criticalRisksHint: "Los que no se arreglan solos",
        demandLine: "{confirmed} ya está confirmado",
        capacityGap: "Faltan {hours} en {centers} centros",
        capacityOk: "La planta llega",
        inventoryLine: "{below} por debajo del stock de seguridad",
        supplyLine: "de {lines} líneas de compra",
        serviceLine: "Estimación del plan, no medida sobre entregas",
        financeLine: "Coste {cost} · margen {margin}",
        riskWhy: "Riesgo crítico en {period}",
        riskWhyNoPeriod: "Riesgo crítico del plan",
        areas: {
          demand: "Demanda",
          capacity: "Capacidad",
          inventory: "Inventario",
          supply: "Suministro",
          service: "Servicio",
          finance: "Finanzas",
        },
      },
      overview: {
        notCalculated: "El plan todavía no se ha calculado",
        notCalculatedHint: "Cálculalo para ver demanda, capacidad, inventario y riesgos.",
        criticalMonth: "{center} no encaja en {period}",
        criticalLine: "Está al {utilisation}: hacen falta {required} y hay {available}.",
        bottlenecks: "Cuellos de botella",
        bottlenecksHint: "Ordenados por cuánto se pasan, no por cuántas horas",
        noBottlenecks: "Ningún centro se pasa de capacidad",
        alerts: "Avisos",
        alertsHint: "Lo que no encaja, con lo que se puede hacer al respecto",
        noRisks: "Ningún riesgo abierto",
        optionHint: "«{option}» se decide en esta pantalla",
      },
      charts: {
        demandVsCapacity: "Demanda contra capacidad",
        demandVsCapacityHint: "Las barras son las horas que pide el plan; la línea, las que tiene la planta",
        requiredHours: "Horas necesarias",
        availableHours: "Horas disponibles",
        sales: "Evolución de ventas",
        salesHint: "Lo que ya está pedido, y lo que es previsión",
        confirmed: "Pedidos confirmados",
        forecast: "Previsión",
        stock: "Stock proyectado",
        stockHint: "Cómo queda el almacén mes a mes",
        projectedStock: "Stock proyectado",
      },
      readiness: {
        title: "Preparación de datos",
        subtitle: "Lo que el plan no ha podido leer. Un cálculo sobre datos incompletos no falla: da un resultado equivocado.",
        ready: "de los datos que hacen falta",
        count: "{ready} de {total}",
        feeds: "Lo que llega de otros módulos",
        feedRows: "{count} registros · último {when}",
        feedEmpty: "Nada todavía",
        checks: {
          routing: "Productos con ruta",
          bom: "Productos con escandallo",
          leadTime: "Materiales con plazo de entrega",
          cost: "Artículos con coste",
          price: "Productos con precio",
          family: "Artículos con familia",
          stock: "Artículos con stock conocido",
          supplier: "Materiales con proveedor",
          calendar: "Centros con calendario",
        },
      },
      feeds: {
        sales_history: "Histórico de ventas",
        order_book: "Cartera de pedidos",
        open_production: "Órdenes de fabricación abiertas",
        open_purchase: "Pedidos de compra abiertos",
        production_actual: "Producción real",
        purchase_actual: "Compras recibidas",
        stock_level: "Existencias",
        absence: "Ausencias aprobadas",
        maintenance: "Mantenimientos previstos",
        asset_stop: "Máquinas paradas",
      },
      quality: {
        itemsWithoutRouting: "Productos sin ruta",
        itemsWithoutBom: "Productos sin escandallo",
        itemsWithoutLeadTime: "Materiales sin plazo",
        itemsWithoutCost: "Artículos sin coste",
        itemsWithoutStock: "Artículos sin stock conocido",
        workCentersWithoutCalendar: "Centros sin calendario",
      },
      demand: {
        item: "Artículo",
        total: "Total",
        consensusTotal: "Demanda S&OP",
        showing: "Qué se ve",
        truncated: "Hay más artículos de los que caben. Filtra por familia para verlos.",
        empty: "No hay nada que planificar",
        emptyHint: "Marca artículos del catálogo como planificables para que aparezcan aquí.",
        generate: "Generar previsión",
        generated: "Previsión generada · {items} de {items} artículos con histórico",
        bulk: "Editar en bloque",
        bulkTitle: "Editar en bloque",
        bulkOperation: "Qué hacer",
        bulkValue: "Valor",
        bulkField: "Sobre qué",
        bulkHint: "Se aplica a lo que hay en pantalla, y nunca a un periodo bloqueado.",
        bulkDone: "{changed} celdas cambiadas",
        bulkDoneSkipped: "{changed} celdas cambiadas · {skipped} bloqueadas sin tocar",
        fields: {
          consensus: "Demanda consensuada",
          confirmed: "Pedidos confirmados",
          baseline: "Previsión",
          pipeline: "Oportunidades CRM",
          adjustment: "Ajuste comercial",
          override: "Cifra decidida",
        },
        hints: {
          consensus: "La suma de todo lo demás, o la cifra decidida cuando hay una. Es lo que planifica el resto del módulo.",
          confirmed: "Viene de los presupuestos aceptados. No se escribe aquí.",
          baseline: "Lo que dice el histórico, o lo que escribas encima.",
          pipeline: "Oportunidades del CRM ponderadas por su probabilidad. No se escribe aquí.",
          adjustment: "Lo que comercial sabe y no está en ninguna de las cifras anteriores.",
          override: "Sustituye la suma en lugar de sumarse a ella. Vacío significa que no hay ninguna.",
        },
        operations: {
          increase: "Subir un porcentaje",
          set: "Poner un valor fijo",
          distribute: "Repartir un total",
          clear: "Vaciar",
        },
      },
      sourcing: {
        make: "Se fabrica",
        buy: "Se compra",
      },
      itemKind: {
        finished: "Producto acabado",
        component: "Componente",
        raw: "Materia prima",
        service: "Servicio",
      },
      centerKind: {
        machine: "Máquina",
        labour: "Personas",
        mixed: "Mixto",
      },
      load: {
        idle: "Sin carga",
        normal: "Normal",
        warning: "Ajustado",
        over: "Sobrecargado",
        critical: "Crítico",
      },
      stock: {
        stockout: "Rotura",
        below_safety: "Bajo mínimos",
        low: "Justo",
        healthy: "Correcto",
        excess: "Exceso",
      },
      supply: {
        showing: "Qué se ve",
        level: "nivel {level}",
        empty: "No hay nada que suministrar",
        emptyHint: "Cálculalo con demanda encima y aquí aparecerá qué hay que fabricar y comprar.",
        problems: "Problemas en los datos",
        problemsHint: "Fallos del escandallo o de la ruta, no riesgos del plan",
        fields: {
          start: "Hay que lanzarlo",
          planned: "Tiene que llegar",
          gross: "Necesidad total",
          independent: "Demanda propia",
          dependent: "Demanda de sus padres",
          scheduled: "Ya en camino",
          projected: "Stock resultante",
        },
        hints: {
          start: "El periodo en que hay que decidirlo, ya descontado el plazo. Es la fila sobre la que se actúa.",
          planned: "El periodo en que hace falta que esté.",
          gross: "Lo que consume el plan entero: lo que se vende y lo que se monta dentro de otra cosa.",
          independent: "Lo que se vende de esto directamente.",
          dependent: "Lo que se lleva por dentro de los productos que lo usan.",
          scheduled: "Órdenes de fabricación y pedidos de compra ya abiertos.",
          projected: "Cómo queda el almacén al final de cada periodo.",
        },
      },
      problems: {
        BOM_CYCLE: "Escandallo circular",
        BOM_ITEM_MISSING: "Componente que no está en el catálogo",
        BOM_TOO_DEEP: "Escandallo demasiado profundo",
        ROUTING_MISSING: "Producto sin ruta",
        WORK_CENTER_MISSING: "Ruta que apunta a un centro que ya no existe",
      },
      capacity: {
        center: "Centro de trabajo",
        heatmap: "Carga por centro y mes",
        heatmapHint: "Toca una celda para ver qué la está cargando",
        detail: "Centros de trabajo",
        detailHint: "Lo que pide el plan contra lo que hay, en todo el horizonte",
        available: "Disponible",
        required: "Necesario",
        gap: "Diferencia",
        peak: "Máximo",
        utilisation: "Utilización",
        calendar: "Calendario",
        calendarBreakdown: "De dónde salen las horas",
        downtime: "Mantenimiento y paradas",
        absence: "Ausencias",
        scenarioHours: "Horas del escenario",
        whatLoadsIt: "Qué lo está cargando",
        nothingLoads: "Nada lo carga en este periodo",
        empty: "No hay centros de trabajo",
        emptyHint: "Sin centros no hay capacidad que calcular: la planta sale vacía.",
      },
      inventory: {
        showing: "Qué se ve",
        worst: "Peor mes",
        safety: "seguridad {quantity}",
        value: "Valor",
        valueChart: "Valor del inventario",
        valueChartHint: "Lo que hay que financiar, mes a mes",
        empty: "No hay inventario que proyectar",
        emptyHint: "Cálculalo con demanda y catálogo encima.",
        excess: "Exceso y obsoletos",
        excessHint: "Lo que el plan no va a consumir",
        excessType: "Tipo",
        quantity: "Cantidad",
        cover: "Cobertura",
        horizons: "{count} veces el horizonte",
        excessKind: {
          dead: "Sin movimiento",
          excess: "Exceso",
        },
        fields: {
          closing: "Stock final",
          demand: "Consumo",
          receipts: "Entradas previstas",
          production: "Reposición planificada",
          coverageDays: "Días de cobertura",
          value: "Valor",
        },
      },
      procurement: {
        hint: "Ordenado por la fecha límite de pedido, no por importe: lo urgente es lo que ya no llega a tiempo.",
        need: "A comprar",
        ofTotal: "de {total} de necesidad total",
        stock: "Stock",
        onOrder: "{quantity} ya pedidos",
        leadTime: "Plazo",
        orderBy: "Pedir antes de",
        risk: "Riesgo",
        cost: "Importe",
        noSupplier: "sin proveedor",
        noLeadTime: "Sin plazo",
        unplaceable: "{count} periodos que ya no se pueden pedir",
        spend: "Compromiso de compra",
        spendHint: "Lo que hay que comprometer cada mes, no lo que llega",
        empty: "No hay nada que comprar",
        emptyHint: "Con la demanda y el stock actuales, el plan se cubre solo.",
        risks: {
          none: "A tiempo",
          tight: "Justo",
          late: "Tarde",
        },
      },
      scenarios: {
        base: "Plan base",
        new: "Nuevo escenario",
        manage: "Escenarios",
        intro: "Un escenario es una pregunta, no una copia del plan: unas pocas palancas sobre los mismos datos.",
        metric: "Métrica",
        name: "Nombre",
        description: "Descripción",
        template: "Punto de partida",
        templateHint: "Rellena las palancas típicas de esa pregunta. Se pueden cambiar.",
        levers: "Palancas",
        lever: "Palanca",
        leverCount: "Palancas",
        addLever: "Añadir palanca",
        noLevers: "Sin palancas: es el plan tal cual",
        pickCenter: "Elegir centro",
        pickItem: "Elegir artículo",
        value: "Valor",
        from: "Desde",
        to: "Hasta",
        fromAny: "Todo el horizonte",
        toAny: "Todo el horizonte",
        active: "En pantalla",
        recommended: "Recomendado",
        approved: "Aprobado",
        read: "Ver con este",
        reading: "El plan se lee con este escenario",
        recommend: "Recomendar",
        recommendedDone: "«{name}» queda como escenario recomendado",
        created: "Escenario creado. Cálculalo para ver sus cifras.",
        removeTitle: "Quitar el escenario",
        removeMessage: "«{name}» y sus cifras dejan de estar. El plan no se toca.",
      },
      templates: {
        base: "Plan base",
        demand_up: "La demanda sube",
        demand_down: "La demanda baja",
        extra_shift: "Un turno más",
        hire: "Contratar",
        subcontract: "Subcontratar",
        new_machine: "Máquina nueva",
        lead_time_up: "Los plazos se alargan",
        machine_down: "Una máquina se para",
        cost_up: "Suben los costes",
      },
      levers: {
        everything: "todo",
        between: "de {from} a {to}",
        demand_pct: "Demanda",
        demand_delta: "Demanda, en unidades",
        capacity_shifts: "Turnos",
        capacity_resources: "Máquinas o personas",
        capacity_hours: "Horas extra",
        capacity_efficiency: "Eficiencia del centro",
        capacity_subcontract: "Horas subcontratables",
        capacity_offline: "Horas que se pierden",
        lead_time_days: "Plazo de entrega",
        purchase_cost_pct: "Coste de compra",
        minimum_order: "Pedido mínimo",
        routing_efficiency: "Eficiencia de la ruta",
        sourcing: "Fabricar o comprar",
        safety_stock: "Stock de seguridad",
        safety_stock_pct: "Stock de seguridad, en porcentaje",
        opening_stock: "Stock inicial",
      },
      metrics: {
        revenue: "Facturación",
        cost: "Coste",
        marginPct: "Margen",
        serviceLevelEstimate: "Nivel de servicio",
        peakUtilisation: "Carga máxima",
        capacityGapHours: "Horas que faltan",
        stockoutItems: "Materiales con rotura",
        peakInventoryValue: "Inversión en stock",
        criticalRisks: "Riesgos críticos",
        good: {
          up: "cuanto más, mejor",
          down: "cuanto menos, mejor",
        },
      },
      severity: {
        low: "Baja",
        medium: "Media",
        high: "Alta",
        critical: "Crítica",
      },
      riskStatus: {
        open: "Abierto",
        accepted: "Asumido",
        mitigating: "En marcha",
        resolved: "Resuelto",
        dismissed: "Descartado",
      },
      riskType: {
        capacity: "Capacidad",
        stock: "Rotura de stock",
        material: "Material",
        supplier: "Proveedor",
        lead_time: "Plazo de entrega",
        demand: "Demanda",
        machine: "Máquina",
        personnel: "Personal",
        skill: "Competencias",
        quality: "Calidad",
        financial: "Financiero",
        logistic: "Logístico",
        data: "Datos",
      },
      risks: {
        options: "Qué se puede hacer",
        status: "Estado del riesgo",
        critical: "Críticos",
        high: "Altos",
        medium: "Medios",
        open: "Abiertos",
        empty: "Ningún riesgo abierto",
        emptyHint: "El plan encaja tal como está.",
        noSupplier: "proveedor sin asignar",
        sentences: {
          capacity: "En {period} está al {utilisation}: hacen falta {required} y hay {available}. Faltan {gap}.",
          stock: "En {period} queda en {closing} con un mínimo de {safety}. Faltan {shortfall}.",
          coverage: "Solo quedan {days} días de cobertura, y el aviso está en {threshold}.",
          leadTime: "Para {period} ya no llega: {days} días de plazo con {supplier}, y hacen falta {quantity}.",
          supplier: "Para {period} había que pedirlo el {orderBy}, y ya han pasado {slack} días. Proveedor: {supplier}.",
          jump: "La previsión sube un {jump} sobre los últimos meses: de {average} a {peak}.",
          bias: "La previsión se desvía siempre en la misma dirección: {bias}, y el aviso está en {threshold}.",
          data: "{count} de {of} artículos no tienen este dato, así que el cálculo no los cuenta bien.",
        },
      },
      options: {
        add_shift: "Añadir un turno",
        subcontract: "Subcontratar",
        hire: "Contratar",
        move_earlier: "Adelantar el trabajo",
        order_now: "Pedirlo ya",
        bring_purchase_forward: "Adelantar la compra",
        raise_safety_stock: "Subir el stock de seguridad",
        alternative_supplier: "Buscar otro proveedor",
        split_delivery: "Partir la entrega",
        confirm_forecast: "Confirmar la previsión",
        review_forecast_process: "Revisar cómo se hace la previsión",
        complete_routings: "Completar las rutas",
        complete_boms: "Completar los escandallos",
        complete_lead_times: "Completar los plazos",
        complete_costs: "Completar los costes",
        import_stock: "Cargar el stock",
        complete_calendars: "Completar los calendarios",
      },
      decisions: {
        title: "Decisiones",
        subtitle: "Qué se decidió y por qué. Es lo que explica el plan seis meses después.",
        record: "Apuntar una decisión",
        recordFor: "Apuntar decisión",
        recorded: "Decisión apuntada",
        what: "Qué se decide",
        whatPlaceholder: "Segundo turno en montaje, semanas 45-48",
        why: "Por qué",
        whyHint: "Obligatorio: una decisión sin motivo no explica nada dentro de seis meses.",
        impact: "Qué se espera que cambie",
        impactHint: "En palabras: «+160 h». La mitad de las decisiones no tienen cifra.",
        hoursImpact: "+{hours} h",
        period: "Periodo",
        anyPeriod: "Todo el plan",
        status: "Estado",
        owner: "Responsable",
        when: "Cuándo",
        empty: "Todavía no hay decisiones apuntadas",
        emptyHint: "Cada cambio del plan que valga la pena explicar cabe aquí en dos líneas.",
        statuses: {
          proposed: "Propuesta",
          approved: "Aprobada",
          rejected: "Rechazada",
          done: "Hecha",
        },
      },
      approve: {
        title: "Aprobar el plan",
        message: "Se congela una versión con las cifras de ahora. No se podrá editar, y cualquier cambio posterior será una versión nueva.",
        messageScenario: "Se aprueba el escenario «{scenario}» y se congela una versión con sus cifras. No se podrá editar.",
        done: "Plan aprobado · versión {version}",
      },
      versions: {
        version: "Versión",
        scenario: "Escenario",
        approvedAt: "Aprobada",
        approvedBy: "Por",
        comment: "Comentario",
        compare: "Comparar versiones",
        compareHint: "Qué cambió entre dos firmas",
        first: "Versión anterior",
        second: "Versión posterior",
        frozen: "Esta versión está congelada: se lee hoy exactamente como el día que se firmó.",
        risksAtApproval: "Riesgos abiertos al aprobar",
        empty: "Todavía no se ha aprobado ninguna versión",
        emptyHint: "Cuando dirección firme el plan, la versión se congela aquí.",
      },
      actual: {
        against: "Comparar contra",
        hint: "Contra la versión {version}, firmada el {when} por {who}.",
        period: "Periodo",
        sales: "Ventas",
        production: "Producción",
        hours: "Horas",
        purchases: "Compras",
        utilisation: "Utilización",
        salesVariance: "Desviación de ventas",
        productionVariance: "Desviación de producción",
        hoursVariance: "Desviación de horas",
        unavailable: "Todavía no hay nada que comparar",
        unavailableHint: "El seguimiento compara contra una versión firmada, y este plan no tiene ninguna.",
        tooEarly: "Todavía no ha pasado ningún periodo",
        tooEarlyHint: "Se compara lo que ya ha ocurrido, nunca lo que está por venir.",
        feeds: "De dónde salen los datos reales",
        feedsHint: "Lo que no está instalado sale con un guion, nunca con un cero",
        feedOn: "Llega",
        feedOff: "No instalado",
        feedNames: {
          sales: "Facturación",
          production: "Producción",
          purchases: "Compras",
        },
      },
      items: {
        title: "Artículos planificables",
        subtitle: "Qué se planifica, cómo se planifica y de qué está hecho",
        hint: "El catálogo del almacén llega entero. Marca solo lo que de verdad se planifica.",
        new: "Nuevo artículo",
        newHint: "Un producto o material que no está en el almacén",
        edit: "Editar artículo",
        item: "Artículo",
        code: "Código",
        name: "Nombre",
        family: "Familia",
        familyHint: "El nivel al que se planifica y se mira. Es la palabra de la empresa, no una lista nuestra.",
        unit: "Unidad",
        kind: "Tipo",
        status: "En el plan",
        inPlan: "Se planifica",
        outOfPlan: "Fuera del plan",
        sourcing: "Origen",
        sourcingHint: "Si se fabrica, carga los centros de trabajo. Si se compra, va al plan de compras.",
        components: "Estructura",
        component: "Componente",
        operations: "{count} operaciones",
        stock: "Stock",
        stockHint: "De dónde arranca la proyección. Llega del almacén cuando lo publica, o se escribe aquí.",
        noStock: "Sin stock conocido",
        missing: "Lo que falta",
        mirrored: "del almacén",
        mirroredHint: "El código, el nombre, la unidad, el coste y el proveedor son del almacén y se actualizan solos. Lo demás es de aquí.",
        fromRecipe: "El escandallo se tomó de la receta {code} del taller. Al editarlo aquí pasa a ser tuyo: la receta ya no lo pisa. Un componente que no está en el catálogo se queda con su nombre, sin enlazar.",
        identity: "Qué es",
        policy: "Cómo se planifica",
        policyHint: "La mitad que no tiene ningún otro módulo del ERP",
        leadTime: "Plazo, en días",
        leadTimeHint: "Desde que se decide hasta que está. Del proveedor si se compra, del taller si se fabrica.",
        safetyStock: "Stock de seguridad",
        safetyStockHint: "El suelo que la proyección no baja nunca.",
        lotSize: "Múltiplo de pedido",
        lotSizeHint: "Se redondea hacia arriba a este múltiplo. Cero significa que no hay.",
        minimumOrder: "Pedido mínimo",
        yield: "Rendimiento",
        yieldHint: "Qué porcentaje sale bien. Al 96 %, hay que empezar más de lo que se necesita.",
        price: "Precio de venta",
        priceHint: "Con lo que se calcula la facturación prevista",
        cost: "Coste",
        supplier: "Proveedor",
        bom: "Escandallo de planificación",
        bomHint: "Al nivel al que se planifica: una máquina lleva un motor y un bastidor, no cuatrocientos tornillos.",
        quantityPerUnit: "Cantidad por unidad",
        scrap: "Merma %",
        pickComponent: "Elegir componente",
        addComponent: "Añadir componente",
        routing: "Ruta",
        routingHint: "Por qué centros pasa y cuánto tarda en cada uno. Sin esto no hay capacidad que calcular.",
        workCenter: "Centro",
        operation: "Operación",
        minutesPerUnit: "Minutos por unidad",
        setupMinutes: "Preparación",
        pickCenter: "Elegir centro",
        addOperation: "Añadir operación",
        totalMinutes: "{minutes} minutos por unidad en total",
        plannedField: "Este artículo entra en el plan",
        plannedFieldHint: "Solo lo marcado se calcula. Un catálogo entero no es un plan.",
        selected: "{count} seleccionados",
        addToPlan: "Meter en el plan",
        removeFromPlan: "Sacar del plan",
        plannedChanged: "{count} artículos actualizados",
        saved: "Artículo guardado",
        searchPlaceholder: "Código, nombre, familia…",
        empty: "El catálogo de planificación está vacío",
        emptyHint: "Con el almacén instalado se llena solo. Sin él, se escribe aquí.",
        archiveTitle: "Archivar el artículo",
        archiveMessage: "«{name}» deja de planificarse. No se borra: los planes firmados lo siguen nombrando.",
        flags: {
          routing: "Sin ruta",
          bom: "Sin escandallo",
          leadTime: "Sin plazo",
          family: "Sin familia",
        },
      },
      centers: {
        title: "Centros de trabajo",
        subtitle: "Dónde se fabrica, y cuántas horas hay de verdad",
        hint: "Ocho horas de calendario no son ocho horas de producción: la eficiencia y la disponibilidad son las que mandan.",
        new: "Nuevo centro",
        edit: "Editar centro",
        center: "Centro",
        code: "Código",
        name: "Nombre",
        kind: "Qué se agota antes",
        kindHint: "Máquina, personas, o las dos cosas. Decide qué le quita una ausencia.",
        allKinds: "Todos",
        skill: "Competencia",
        skillHint: "Una palabra, la de la empresa: «soldadura certificada». Agrupa centros para leer una falta como falta de algo.",
        calendar: "Calendario",
        calendarLine: "{hours} h × {shifts} turnos × {resources}",
        daysPerWeek: "{days} días a la semana",
        hoursPerShift: "Horas por turno",
        shifts: "Turnos",
        daysPerWeekField: "Días a la semana",
        resources: "Cuántos a la vez",
        resourcesHint: "Cuatro CNC, o seis soldadores. Multiplica las horas.",
        factors: "Eficiencia y disponibilidad",
        factorsHint: "Dos cosas distintas y de dos personas distintas: cuánto de una hora en la máquina es trabajo, y cuánto tiempo la máquina está en condiciones de ir.",
        efficiency: "Eficiencia %",
        availability: "Disponibilidad %",
        maintenanceHours: "Horas por mantenimiento",
        maintenanceHoursHint: "Lo que cuesta un mantenimiento preventivo aquí. Mantenimiento avisa de que vence; cuántas horas se lleva solo lo sabe la planta.",
        money: "Coste de la hora",
        costPerHour: "Hora normal",
        overtimeCost: "Hora extra",
        subcontractCost: "Hora subcontratada",
        subcontractHours: "Horas que puede absorber fuera",
        subcontractHoursHint: "Cero significa que este trabajo no sale de la planta, que es la respuesta habitual.",
        monthlyCapacity: "Capacidad al mes",
        ofCalendar: "de {hours} de calendario",
        links: "Con qué está enganchado",
        crewCount: "{count} personas",
        assetCount: "{count} máquinas",
        subcontract: "{hours} h fuera",
        preview: "Con estos números, un mes normal da {hours}.",
        saved: "Centro guardado",
        searchPlaceholder: "Código, nombre, competencia…",
        empty: "Todavía no hay centros de trabajo",
        emptyHint: "Son de este módulo: ningún otro sitio del ERP sabe cuántas horas tiene la planta.",
        archiveTitle: "Archivar el centro",
        archiveMessage: "«{name}» sale de todas las rutas que lo nombraban. Si no, esas horas desaparecerían del plan sin avisar.",
      },
      form: {
        name: "Nombre del plan",
        namePlaceholder: "S&OP 2027",
        nameRequired: "El plan necesita un nombre",
        description: "Descripción",
        site: "Planta",
        siteHint: "El centro de trabajo de la empresa al que se refiere este plan. En una instancia de una sola planta, se puede dejar vacío.",
        sitePlaceholder: "Planta 1",
        siteLinked: "Planta de la empresa",
        siteUnlink: "Escribir el nombre a mano",
        from: "Empieza en",
        fromRequired: "Hace falta un mes de inicio",
        months: "Cuántos meses",
        monthsHint: "Doce es lo normal en fabricación. Más, cuando los plazos son tan largos que un año no llega para decidir nada.",
        monthsCount: "{count} meses",
        granularity: "Se planifica por",
        granularityHint: "Meses casi siempre: esto es el plan táctico, no el programa del taller.",
        currency: "Moneda",
        range: "De {from} a {to}",
        capacityWarn: "Aviso de capacidad %",
        capacityWarnHint: "Por debajo, el mes es normal. Es un aviso, no un límite: no se rechaza nada.",
        capacityOver: "Sobrecarga %",
        capacityCritical: "Crítico %",
        frozenWeeks: "Semanas congeladas",
        frozenWeeksHint: "Los meses de cerca, donde el material ya está comprado y cambiar cuesta dinero.",
        firmMonths: "Meses firmes",
        usePipeline: "Contar las oportunidades del CRM",
        usePipelineHint: "Desactivado a propósito: una empresa que presupuesta todo contaría la misma máquina dos veces, como oportunidad y como presupuesto aceptado.",
      },
      wizard: {
        step1: "1 · Qué plan es",
        step1Hint: "El nombre con el que se hablará de él en la reunión",
        step2: "2 · Hasta dónde mira",
        step2Hint: "El horizonte y el tamaño del cubo",
        step3: "3 · Con qué se juzga",
        step3Hint: "Se copian en el plan: una versión aprobada conserva los límites con los que se firmó.",
        step4: "4 · Hay algo que planificar",
        step4Hint: "Un plan sobre un catálogo vacío calcula una planta vacía, y eso se parece mucho a una respuesta.",
        plannedItems: "Artículos en el plan",
        workCenters: "Centros de trabajo",
        missingMasters: "Sin artículos planificables o sin centros de trabajo, el cálculo no dirá nada útil.",
      },
      demo: {
        created: "Fábrica de ejemplo creada: {items} artículos y {centers} centros",
        labels: {
          site: "Planta 1",
          planDescription: "Plan de demostración: fabricación industrial de maquinaria",
          extraShift: "Turno extra en montaje",
          extraShiftHint: "Un segundo turno en montaje durante el mes crítico",
          subcontract: "Subcontratar montaje",
          subcontractHint: "Sacar fuera parte del montaje, con el coste que eso tiene",
          demandUp: "Demanda +15 %",
          demandUpHint: "Qué pasa si el año va mejor de lo previsto",
        },
      },
      dashboard: {
        peak: "Carga máxima prevista",
        peakHelp: "La utilización del centro más cargado, en su peor mes, de todos los planes abiertos.",
        risks: "Riesgos críticos",
        risksHelp: "Cuántos riesgos críticos siguen abiertos en los planes que no están cerrados.",
        gap: "Horas que faltan",
        gapHelp: "La suma de las horas que los planes abiertos piden por encima de lo que hay.",
        revenue: "Facturación planificada",
        revenueHelp: "Lo que suman las previsiones de los planes abiertos, a los precios del catálogo.",
        riskList: "Los riesgos críticos",
        riskListHelp: "Qué está en riesgo, en qué plan y en qué mes. Estira la tarjeta y caben más.",
        stale: "Planes por recalcular",
        staleHelp: "Los planes cuyas cifras ya no corresponden a los datos que tienen debajo.",
      },
    },
    permissions: {
      sop: {
        read: "Ver los planes S&OP",
        demand: "Trabajar la demanda: previsión, ajustes y consenso",
        supply: "Trabajar el suministro: catálogo, rutas, centros de trabajo y cálculo",
        scenarios: "Crear y comparar escenarios",
        approve: "Aprobar el plan y apuntar decisiones",
        costs: "Ver lo que vale el plan: facturación, coste y margen",
        admin: "Abrir planes, configurarlos y archivarlos",
      },
    },
    audit: {
      actions: {
        sop: {
          plan_created: "Plan S&OP creado",
          plan_updated: "Plan S&OP modificado",
          plan_stage_changed: "Plan pasado a otra fase del ciclo",
          plan_calculated: "Plan S&OP calculado",
          plan_approved: "Plan S&OP aprobado",
          plan_closed: "Plan S&OP cerrado",
          plan_archived: "Plan S&OP archivado",
          plan_restored: "Plan S&OP devuelto a las listas",
          plan_commented: "Comentario en el plan",
          plan_comment_removed: "Comentario borrado del plan",
          demand_edited: "Celda de demanda modificada",
          demand_bulk_edited: "Demanda modificada en bloque",
          demand_locked: "Periodos de demanda bloqueados",
          baseline_generated: "Previsión generada del histórico",
          item_created: "Artículo de planificación creado",
          item_updated: "Artículo de planificación modificado",
          item_archived: "Artículo de planificación archivado",
          item_restored: "Artículo devuelto al catálogo",
          items_planned: "Artículos metidos o sacados del plan",
          item_stock_set: "Stock de partida corregido",
          work_center_created: "Centro de trabajo creado",
          work_center_updated: "Centro de trabajo modificado",
          work_center_archived: "Centro de trabajo archivado",
          work_center_restored: "Centro de trabajo devuelto al catálogo",
          scenario_created: "Escenario creado",
          scenario_updated: "Escenario modificado",
          scenario_archived: "Escenario quitado",
          scenario_activated: "Escenario puesto en pantalla",
          scenario_recommended: "Escenario recomendado",
          risk_raised: "Riesgo apuntado a mano",
          risk_updated: "Riesgo actualizado",
          decision_recorded: "Decisión S&OP apuntada",
          decision_updated: "Decisión S&OP actualizada",
          demo_seeded: "Datos de ejemplo creados",
        },
      },
    },
    assistant: {
      openers: {
        sop: {
          risks: "¿Qué riesgos tenemos en noviembre?",
          bottleneck: "¿Dónde está el cuello de botella del plan?",
          scenarios: "Compara los escenarios del plan actual",
        },
      },
    },
    settings: {
      ns: {
        sop: {
          title: "Planificación S&OP",
          subtitle: "Cómo planifica esta empresa: horizonte, umbrales y qué cuenta como demanda",
          defaultHorizonMonths: {
            label: "Meses que mira un plan nuevo",
            help: "Doce es la respuesta habitual en fabricación. Dieciocho o veinticuatro si los plazos son tan largos que un año no llega para decidir nada.",
          },
          defaultGranularity: {
            label: "El cubo de un plan nuevo",
            help: "Meses casi siempre: esto es el plan táctico, no el programa del taller. Semanas solo donde el horizonte es corto y las decisiones son así de finas.",
          },
          currency: {
            label: "Moneda en que se valoran los planes",
            help: "Código ISO. Es en lo que se expresan la facturación, el coste y el margen de un plan.",
          },
          capacityWarnPct: {
            label: "Utilización a partir de la cual vigilar un centro",
            help: "Por debajo, el periodo es normal. Es un aviso y no un límite: no se rechaza nada.",
          },
          capacityOverPct: {
            label: "Utilización a partir de la cual un centro no cabe",
            help: "Cien significa que el plan pide más horas de las que tiene la planta. Algunas plantas ponen su referencia por encima a propósito, porque sus tiempos estándar son conservadores.",
          },
          capacityCriticalPct: {
            label: "Utilización a partir de la cual es un riesgo crítico",
            help: "Pasado esto, el plan levanta un riesgo crítico y el bus lo lleva. Ponlo donde las horas extra y algo de subcontratación dejan de ser suficientes.",
          },
          coverageWarnDays: {
            label: "Días de cobertura por debajo de los cuales avisar",
            help: "Se mide contra el consumo que VIENE DESPUÉS del saldo, nunca contra el pasado. Un material sin demanda por delante no tiene cobertura ni aviso.",
          },
          forecastBiasWarnPct: {
            label: "Desviación sistemática que ya es un problema",
            help: "El sesgo es el error medio CON SIGNO: una previsión equivocada un 15 % en la misma dirección mes tras mes es un proceso roto, no mala suerte.",
          },
          serviceTargetPct: {
            label: "Nivel de servicio al que apunta la empresa",
            help: "Contra lo que se compara el plan. La cifra que produce es una ESTIMACIÓN a partir del propio plan: la medida sale de las entregas, que este módulo no ve.",
          },
          frozenWeeks: {
            label: "Semanas de horizonte congelado",
            help: "Los periodos de cerca, donde el material ya está comprado y cambiar cuesta dinero. Colorea la rejilla; bloquear una celda sigue siendo una decisión que toma alguien.",
          },
          firmMonths: {
            label: "Meses de horizonte firme tras el congelado",
            help: "Aquí los cambios son controlados, no imposibles. Todo lo que viene después es flexible.",
          },
          usePipeline: {
            label: "Contar las oportunidades del CRM en la demanda",
            help: "Desactivado a propósito: una empresa que presupuesta todo contaría la misma máquina dos veces, como oportunidad y como el presupuesto aceptado en que se convirtió. Actívalo solo donde las oportunidades no acaban en presupuesto.",
          },
        },
      },
    },
  },
  ca: {
    sop: {
      title: "Planificació S&OP",
      search: {
        subtitle: "Pla tàctic",
      },
      notifications: {
        categories: {
          planning: "Planificació S&OP",
        },
      },
      units: {
        days: "{count} dies",
      },
      actions: {
        newPlan: "Nou pla",
        createPlan: "Crear el pla",
        calculate: "Calcular",
        approve: "Aprovar",
        handTo: "Passar a {stage}",
        save: "Desar",
        cancel: "Cancel·lar",
        apply: "Aplicar",
        remove: "Treure",
        archive: "Arxivar",
        openCatalogue: "Anar al catàleg",
        openWorkCenters: "Anar a centres de treball",
        seedDemo: "Crear dades d\u0027exemple",
      },
      pager: {
        previous: "Anterior",
        next: "Següent",
        of: "Pàgina {page} de {pages}",
      },
      empty: {
        none: "Encara no hi ha cap pla S&OP",
        noneHint: "Crea el primer per analitzar demanda, capacitat i inventari dels propers mesos.",
        filtered: "Sense resultats",
        filteredHint: "Prova a treure algun filtre o a buscar una altra cosa.",
      },
      status: {
        draft: "Esborrany",
        preparation: "Preparació",
        demand_review: "Revisió de demanda",
        supply_review: "Revisió de subministrament",
        capacity_review: "Revisió de capacitat",
        scenario_review: "Revisió d\u0027escenaris",
        financial_review: "Revisió financera",
        executive_review: "Reunió de direcció",
        approved: "Aprovat",
        closed: "Tancat",
      },
      cycle: {
        title: "Cicle S&OP",
        moved: "El pla passa a {stage}",
      },
      calc: {
        never: "Sense calcular",
        stale: "Desactualitzat",
        running: "Calculant",
        ready: "Actualitzat",
        failed: "Error de càlcul",
        lastRun: "Últim càlcul: {when}",
        done: "Pla calculat en {ms} ms · {risks} riscos",
        notice: {
          never: "Aquest pla encara no s\u0027ha calculat: les pantalles són buides fins que ho facis.",
          stale: "Alguna cosa ha canviat des de l\u0027últim càlcul. Les xifres que veus són les d\u0027abans.",
          running: "El pla s\u0027està calculant.",
          ready: "Les xifres són al dia.",
          failed: "L\u0027últim càlcul va fallar. Revisa la preparació de dades i torna-ho a provar.",
        },
      },
      granularity: {
        month: "Mes",
        week: "Setmana",
        quarter: "Trimestre",
      },
      fence: {
        frozen: "congelat",
        firm: "ferm",
        flexible: "flexible",
      },
      plans: {
        title: "Plans",
        subtitle: "Els plans S&OP de l\u0027empresa, i en quin punt del cicle és cadascun",
        detailTitle: "Pla S&OP",
        newTitle: "Nou pla S&OP",
        newSubtitle: "Què es planifica, fins quan i amb quins límits",
        versionLabel: "v{version}",
        created: "Pla {code} creat",
      },
      table: {
        code: "Codi",
        name: "Pla",
        horizon: "Horitzó",
        status: "Estat",
        calc: "Càlcul",
        version: "Versió",
      },
      tabs: {
        overview: "Resum",
        demand: "Demanda",
        supply: "Subministrament",
        capacity: "Capacitat",
        inventory: "Inventari",
        procurement: "Compres",
        scenarios: "Escenaris",
        risks: "Riscos",
        versions: "Pla aprovat",
        actual: "Seguiment",
      },
      filters: {
        search: "Cercar",
        searchPlaceholder: "Codi o nom del pla…",
        status: "Estat",
        allStatuses: "Tots",
        family: "Família",
        allFamilies: "Totes les famílies",
        sourcing: "Origen",
        allSourcings: "Fabricat i comprat",
        planned: "Al pla",
        allItems: "Tot el catàleg",
        state: "Situació",
        allStates: "Totes les situacions",
        risk: "Risc",
        allRisks: "Tots",
        severity: "Gravetat",
        allSeverities: "Totes",
        riskType: "Tipus",
        allTypes: "Tots els tipus",
      },
      kpi: {
        openPlans: "Plans oberts",
        approvedPlans: "Plans aprovats",
        closedPlans: "Plans tancats",
        needsCalc: "Pendents de calcular",
        demand: "Demanda prevista",
        confirmed: "Ja confirmat",
        peakUtilisation: "Utilització màxima",
        capacityGap: "Hores que falten",
        stockouts: "Materials amb ruptura",
        service: "Nivell de servei estimat",
        purchaseRisk: "Compres en risc",
        revenue: "Facturació prevista",
        cost: "Cost previst",
        margin: "Marge previst",
        inventoryValue: "Inventari màxim",
      },
      cockpit: {
        title: "Resum S&OP",
        subtitle: "Com va el pla i què cal decidir",
        pick: "Pla",
        openPlan: "Obrir el pla",
        decisions: "Decisions que cal prendre",
        decisionsHint: "El que ja s\u0027ha escrit primer, i després els riscos crítics que ningú ha decidit encara",
        noDecisions: "Res pendent de decidir",
        criticalRisks: "Riscos crítics",
        criticalRisksHint: "Els que no s\u0027arreglen sols",
        demandLine: "{confirmed} ja està confirmat",
        capacityGap: "Falten {hours} en {centers} centres",
        capacityOk: "La planta hi arriba",
        inventoryLine: "{below} per sota de l\u0027estoc de seguretat",
        supplyLine: "de {lines} línies de compra",
        serviceLine: "Estimació del pla, no mesurada sobre lliuraments",
        financeLine: "Cost {cost} · marge {margin}",
        riskWhy: "Risc crític a {period}",
        riskWhyNoPeriod: "Risc crític del pla",
        areas: {
          demand: "Demanda",
          capacity: "Capacitat",
          inventory: "Inventari",
          supply: "Subministrament",
          service: "Servei",
          finance: "Finances",
        },
      },
      overview: {
        notCalculated: "El pla encara no s\u0027ha calculat",
        notCalculatedHint: "Calcula\u0027l per veure demanda, capacitat, inventari i riscos.",
        criticalMonth: "{center} no encaixa a {period}",
        criticalLine: "És al {utilisation}: calen {required} i n\u0027hi ha {available}.",
        bottlenecks: "Colls d\u0027ampolla",
        bottlenecksHint: "Ordenats per quant es passen, no per quantes hores",
        noBottlenecks: "Cap centre es passa de capacitat",
        alerts: "Avisos",
        alertsHint: "El que no encaixa, amb el que s\u0027hi pot fer",
        noRisks: "Cap risc obert",
        optionHint: "«{option}» es decideix en aquesta pantalla",
      },
      charts: {
        demandVsCapacity: "Demanda contra capacitat",
        demandVsCapacityHint: "Les barres són les hores que demana el pla; la línia, les que té la planta",
        requiredHours: "Hores necessàries",
        availableHours: "Hores disponibles",
        sales: "Evolució de vendes",
        salesHint: "El que ja està demanat, i el que és previsió",
        confirmed: "Comandes confirmades",
        forecast: "Previsió",
        stock: "Estoc projectat",
        stockHint: "Com queda el magatzem mes a mes",
        projectedStock: "Estoc projectat",
      },
      readiness: {
        title: "Preparació de dades",
        subtitle: "El que el pla no ha pogut llegir. Un càlcul sobre dades incompletes no falla: dona un resultat equivocat.",
        ready: "de les dades que calen",
        count: "{ready} de {total}",
        feeds: "El que arriba d\u0027altres mòduls",
        feedRows: "{count} registres · últim {when}",
        feedEmpty: "Res encara",
        checks: {
          routing: "Productes amb ruta",
          bom: "Productes amb escandall",
          leadTime: "Materials amb termini de lliurament",
          cost: "Articles amb cost",
          price: "Productes amb preu",
          family: "Articles amb família",
          stock: "Articles amb estoc conegut",
          supplier: "Materials amb proveïdor",
          calendar: "Centres amb calendari",
        },
      },
      feeds: {
        sales_history: "Històric de vendes",
        order_book: "Cartera de comandes",
        open_production: "Ordres de fabricació obertes",
        open_purchase: "Comandes de compra obertes",
        production_actual: "Producció real",
        purchase_actual: "Compres rebudes",
        stock_level: "Existències",
        absence: "Absències aprovades",
        maintenance: "Manteniments previstos",
        asset_stop: "Màquines aturades",
      },
      quality: {
        itemsWithoutRouting: "Productes sense ruta",
        itemsWithoutBom: "Productes sense escandall",
        itemsWithoutLeadTime: "Materials sense termini",
        itemsWithoutCost: "Articles sense cost",
        itemsWithoutStock: "Articles sense estoc conegut",
        workCentersWithoutCalendar: "Centres sense calendari",
      },
      demand: {
        item: "Article",
        total: "Total",
        consensusTotal: "Demanda S&OP",
        showing: "Què es veu",
        truncated: "Hi ha més articles dels que hi caben. Filtra per família per veure\u0027ls.",
        empty: "No hi ha res a planificar",
        emptyHint: "Marca articles del catàleg com a planificables perquè apareguin aquí.",
        generate: "Generar previsió",
        generated: "Previsió generada · {items} de {items} articles amb històric",
        bulk: "Editar en bloc",
        bulkTitle: "Editar en bloc",
        bulkOperation: "Què fer",
        bulkValue: "Valor",
        bulkField: "Sobre què",
        bulkHint: "S\u0027aplica al que hi ha a la pantalla, i mai a un període bloquejat.",
        bulkDone: "{changed} cel·les canviades",
        bulkDoneSkipped: "{changed} cel·les canviades · {skipped} bloquejades sense tocar",
        fields: {
          consensus: "Demanda consensuada",
          confirmed: "Comandes confirmades",
          baseline: "Previsió",
          pipeline: "Oportunitats CRM",
          adjustment: "Ajust comercial",
          override: "Xifra decidida",
        },
        hints: {
          consensus: "La suma de tota la resta, o la xifra decidida quan n\u0027hi ha una. És el que planifica la resta del mòdul.",
          confirmed: "Ve dels pressupostos acceptats. No s\u0027escriu aquí.",
          baseline: "El que diu l\u0027històric, o el que hi escriguis a sobre.",
          pipeline: "Oportunitats del CRM ponderades per la seva probabilitat. No s\u0027escriu aquí.",
          adjustment: "El que comercial sap i no és en cap de les xifres anteriors.",
          override: "Substitueix la suma en lloc de sumar-s\u0027hi. Buit vol dir que no n\u0027hi ha cap.",
        },
        operations: {
          increase: "Pujar un percentatge",
          set: "Posar un valor fix",
          distribute: "Repartir un total",
          clear: "Buidar",
        },
      },
      sourcing: {
        make: "Es fabrica",
        buy: "Es compra",
      },
      itemKind: {
        finished: "Producte acabat",
        component: "Component",
        raw: "Matèria primera",
        service: "Servei",
      },
      centerKind: {
        machine: "Màquina",
        labour: "Persones",
        mixed: "Mixt",
      },
      load: {
        idle: "Sense càrrega",
        normal: "Normal",
        warning: "Ajustat",
        over: "Sobrecarregat",
        critical: "Crític",
      },
      stock: {
        stockout: "Ruptura",
        below_safety: "Sota mínims",
        low: "Just",
        healthy: "Correcte",
        excess: "Excés",
      },
      supply: {
        showing: "Què es veu",
        level: "nivell {level}",
        empty: "No hi ha res a subministrar",
        emptyHint: "Calcula\u0027l amb demanda a sobre i aquí apareixerà què cal fabricar i comprar.",
        problems: "Problemes a les dades",
        problemsHint: "Errors de l\u0027escandall o de la ruta, no riscos del pla",
        fields: {
          start: "Cal llançar-ho",
          planned: "Ha d\u0027arribar",
          gross: "Necessitat total",
          independent: "Demanda pròpia",
          dependent: "Demanda dels seus pares",
          scheduled: "Ja en camí",
          projected: "Estoc resultant",
        },
        hints: {
          start: "El període en què cal decidir-ho, ja descomptat el termini. És la fila sobre la qual s\u0027actua.",
          planned: "El període en què cal que hi sigui.",
          gross: "El que consumeix el pla sencer: el que es ven i el que es munta dins d\u0027una altra cosa.",
          independent: "El que es ven d\u0027això directament.",
          dependent: "El que se\u0027n va dins dels productes que l\u0027utilitzen.",
          scheduled: "Ordres de fabricació i comandes de compra ja oberts.",
          projected: "Com queda el magatzem al final de cada període.",
        },
      },
      problems: {
        BOM_CYCLE: "Escandall circular",
        BOM_ITEM_MISSING: "Component que no és al catàleg",
        BOM_TOO_DEEP: "Escandall massa profund",
        ROUTING_MISSING: "Producte sense ruta",
        WORK_CENTER_MISSING: "Ruta que apunta a un centre que ja no existeix",
      },
      capacity: {
        center: "Centre de treball",
        heatmap: "Càrrega per centre i mes",
        heatmapHint: "Toca una cel·la per veure què l\u0027està carregant",
        detail: "Centres de treball",
        detailHint: "El que demana el pla contra el que hi ha, en tot l\u0027horitzó",
        available: "Disponible",
        required: "Necessari",
        gap: "Diferència",
        peak: "Màxim",
        utilisation: "Utilització",
        calendar: "Calendari",
        calendarBreakdown: "D\u0027on surten les hores",
        downtime: "Manteniment i aturades",
        absence: "Absències",
        scenarioHours: "Hores de l\u0027escenari",
        whatLoadsIt: "Què l\u0027està carregant",
        nothingLoads: "Res no el carrega en aquest període",
        empty: "No hi ha centres de treball",
        emptyHint: "Sense centres no hi ha capacitat a calcular: la planta surt buida.",
      },
      inventory: {
        showing: "Què es veu",
        worst: "Pitjor mes",
        safety: "seguretat {quantity}",
        value: "Valor",
        valueChart: "Valor de l\u0027inventari",
        valueChartHint: "El que cal finançar, mes a mes",
        empty: "No hi ha inventari a projectar",
        emptyHint: "Calcula\u0027l amb demanda i catàleg a sobre.",
        excess: "Excés i obsolets",
        excessHint: "El que el pla no consumirà",
        excessType: "Tipus",
        quantity: "Quantitat",
        cover: "Cobertura",
        horizons: "{count} vegades l\u0027horitzó",
        excessKind: {
          dead: "Sense moviment",
          excess: "Excés",
        },
        fields: {
          closing: "Estoc final",
          demand: "Consum",
          receipts: "Entrades previstes",
          production: "Reposició planificada",
          coverageDays: "Dies de cobertura",
          value: "Valor",
        },
      },
      procurement: {
        hint: "Ordenat per la data límit de comanda, no per import: l\u0027urgent és el que ja no arriba a temps.",
        need: "A comprar",
        ofTotal: "de {total} de necessitat total",
        stock: "Estoc",
        onOrder: "{quantity} ja demanats",
        leadTime: "Termini",
        orderBy: "Demanar abans de",
        risk: "Risc",
        cost: "Import",
        noSupplier: "sense proveïdor",
        noLeadTime: "Sense termini",
        unplaceable: "{count} períodes que ja no es poden demanar",
        spend: "Compromís de compra",
        spendHint: "El que cal comprometre cada mes, no el que arriba",
        empty: "No hi ha res a comprar",
        emptyHint: "Amb la demanda i l\u0027estoc actuals, el pla es cobreix sol.",
        risks: {
          none: "A temps",
          tight: "Just",
          late: "Tard",
        },
      },
      scenarios: {
        base: "Pla base",
        new: "Nou escenari",
        manage: "Escenaris",
        intro: "Un escenari és una pregunta, no una còpia del pla: unes poques palanques sobre les mateixes dades.",
        metric: "Mètrica",
        name: "Nom",
        description: "Descripció",
        template: "Punt de partida",
        templateHint: "Omple les palanques típiques d\u0027aquesta pregunta. Es poden canviar.",
        levers: "Palanques",
        lever: "Palanca",
        leverCount: "Palanques",
        addLever: "Afegir palanca",
        noLevers: "Sense palanques: és el pla tal com és",
        pickCenter: "Triar centre",
        pickItem: "Triar article",
        value: "Valor",
        from: "Des de",
        to: "Fins a",
        fromAny: "Tot l\u0027horitzó",
        toAny: "Tot l\u0027horitzó",
        active: "A la pantalla",
        recommended: "Recomanat",
        approved: "Aprovat",
        read: "Veure amb aquest",
        reading: "El pla es llegeix amb aquest escenari",
        recommend: "Recomanar",
        recommendedDone: "«{name}» queda com a escenari recomanat",
        created: "Escenari creat. Calcula\u0027l per veure\u0027n les xifres.",
        removeTitle: "Treure l\u0027escenari",
        removeMessage: "«{name}» i les seves xifres deixen d\u0027existir. El pla no es toca.",
      },
      templates: {
        base: "Pla base",
        demand_up: "La demanda puja",
        demand_down: "La demanda baixa",
        extra_shift: "Un torn més",
        hire: "Contractar",
        subcontract: "Subcontractar",
        new_machine: "Màquina nova",
        lead_time_up: "Els terminis s\u0027allarguen",
        machine_down: "Una màquina s\u0027atura",
        cost_up: "Pugen els costos",
      },
      levers: {
        everything: "tot",
        between: "de {from} a {to}",
        demand_pct: "Demanda",
        demand_delta: "Demanda, en unitats",
        capacity_shifts: "Torns",
        capacity_resources: "Màquines o persones",
        capacity_hours: "Hores extra",
        capacity_efficiency: "Eficiència del centre",
        capacity_subcontract: "Hores subcontractables",
        capacity_offline: "Hores que es perden",
        lead_time_days: "Termini de lliurament",
        purchase_cost_pct: "Cost de compra",
        minimum_order: "Comanda mínima",
        routing_efficiency: "Eficiència de la ruta",
        sourcing: "Fabricar o comprar",
        safety_stock: "Estoc de seguretat",
        safety_stock_pct: "Estoc de seguretat, en percentatge",
        opening_stock: "Estoc inicial",
      },
      metrics: {
        revenue: "Facturació",
        cost: "Cost",
        marginPct: "Marge",
        serviceLevelEstimate: "Nivell de servei",
        peakUtilisation: "Càrrega màxima",
        capacityGapHours: "Hores que falten",
        stockoutItems: "Materials amb ruptura",
        peakInventoryValue: "Inversió en estoc",
        criticalRisks: "Riscos crítics",
        good: {
          up: "com més, millor",
          down: "com menys, millor",
        },
      },
      severity: {
        low: "Baixa",
        medium: "Mitjana",
        high: "Alta",
        critical: "Crítica",
      },
      riskStatus: {
        open: "Obert",
        accepted: "Assumit",
        mitigating: "En marxa",
        resolved: "Resolt",
        dismissed: "Descartat",
      },
      riskType: {
        capacity: "Capacitat",
        stock: "Ruptura d\u0027estoc",
        material: "Material",
        supplier: "Proveïdor",
        lead_time: "Termini de lliurament",
        demand: "Demanda",
        machine: "Màquina",
        personnel: "Personal",
        skill: "Competències",
        quality: "Qualitat",
        financial: "Financer",
        logistic: "Logístic",
        data: "Dades",
      },
      risks: {
        options: "Què s\u0027hi pot fer",
        status: "Estat del risc",
        critical: "Crítics",
        high: "Alts",
        medium: "Mitjans",
        open: "Oberts",
        empty: "Cap risc obert",
        emptyHint: "El pla encaixa tal com és.",
        noSupplier: "proveïdor sense assignar",
        sentences: {
          capacity: "A {period} és al {utilisation}: calen {required} i n\u0027hi ha {available}. En falten {gap}.",
          stock: "A {period} queda en {closing} amb un mínim de {safety}. En falten {shortfall}.",
          coverage: "Només queden {days} dies de cobertura, i l\u0027avís és a {threshold}.",
          leadTime: "Per a {period} ja no hi arriba: {days} dies de termini amb {supplier}, i en calen {quantity}.",
          supplier: "Per a {period} calia demanar-ho el {orderBy}, i ja han passat {slack} dies. Proveïdor: {supplier}.",
          jump: "La previsió puja un {jump} sobre els últims mesos: de {average} a {peak}.",
          bias: "La previsió es desvia sempre en la mateixa direcció: {bias}, i l\u0027avís és a {threshold}.",
          data: "{count} de {of} articles no tenen aquesta dada, així que el càlcul no els compta bé.",
        },
      },
      options: {
        add_shift: "Afegir un torn",
        subcontract: "Subcontractar",
        hire: "Contractar",
        move_earlier: "Avançar la feina",
        order_now: "Demanar-ho ja",
        bring_purchase_forward: "Avançar la compra",
        raise_safety_stock: "Pujar l\u0027estoc de seguretat",
        alternative_supplier: "Buscar un altre proveïdor",
        split_delivery: "Partir el lliurament",
        confirm_forecast: "Confirmar la previsió",
        review_forecast_process: "Revisar com es fa la previsió",
        complete_routings: "Completar les rutes",
        complete_boms: "Completar els escandalls",
        complete_lead_times: "Completar els terminis",
        complete_costs: "Completar els costos",
        import_stock: "Carregar l\u0027estoc",
        complete_calendars: "Completar els calendaris",
      },
      decisions: {
        title: "Decisions",
        subtitle: "Què es va decidir i per què. És el que explica el pla sis mesos després.",
        record: "Apuntar una decisió",
        recordFor: "Apuntar decisió",
        recorded: "Decisió apuntada",
        what: "Què es decideix",
        whatPlaceholder: "Segon torn a muntatge, setmanes 45-48",
        why: "Per què",
        whyHint: "Obligatori: una decisió sense motiu no explica res d\u0027aquí a sis mesos.",
        impact: "Què s\u0027espera que canviï",
        impactHint: "En paraules: «+160 h». La meitat de les decisions no tenen xifra.",
        hoursImpact: "+{hours} h",
        period: "Període",
        anyPeriod: "Tot el pla",
        status: "Estat",
        owner: "Responsable",
        when: "Quan",
        empty: "Encara no hi ha decisions apuntades",
        emptyHint: "Cada canvi del pla que valgui la pena explicar hi cap en dues línies.",
        statuses: {
          proposed: "Proposta",
          approved: "Aprovada",
          rejected: "Rebutjada",
          done: "Feta",
        },
      },
      approve: {
        title: "Aprovar el pla",
        message: "Es congela una versió amb les xifres d\u0027ara. No es podrà editar, i qualsevol canvi posterior serà una versió nova.",
        messageScenario: "S\u0027aprova l\u0027escenari «{scenario}» i es congela una versió amb les seves xifres. No es podrà editar.",
        done: "Pla aprovat · versió {version}",
      },
      versions: {
        version: "Versió",
        scenario: "Escenari",
        approvedAt: "Aprovada",
        approvedBy: "Per",
        comment: "Comentari",
        compare: "Comparar versions",
        compareHint: "Què va canviar entre dues signatures",
        first: "Versió anterior",
        second: "Versió posterior",
        frozen: "Aquesta versió està congelada: es llegeix avui exactament com el dia que es va signar.",
        risksAtApproval: "Riscos oberts en aprovar",
        empty: "Encara no s\u0027ha aprovat cap versió",
        emptyHint: "Quan direcció signi el pla, la versió es congela aquí.",
      },
      actual: {
        against: "Comparar contra",
        hint: "Contra la versió {version}, signada el {when} per {who}.",
        period: "Període",
        sales: "Vendes",
        production: "Producció",
        hours: "Hores",
        purchases: "Compres",
        utilisation: "Utilització",
        salesVariance: "Desviació de vendes",
        productionVariance: "Desviació de producció",
        hoursVariance: "Desviació d\u0027hores",
        unavailable: "Encara no hi ha res a comparar",
        unavailableHint: "El seguiment compara contra una versió signada, i aquest pla no en té cap.",
        tooEarly: "Encara no ha passat cap període",
        tooEarlyHint: "Es compara el que ja ha passat, mai el que està per venir.",
        feeds: "D\u0027on surten les dades reals",
        feedsHint: "El que no està instal·lat surt amb un guió, mai amb un zero",
        feedOn: "Arriba",
        feedOff: "No instal·lat",
        feedNames: {
          sales: "Facturació",
          production: "Producció",
          purchases: "Compres",
        },
      },
      items: {
        title: "Articles planificables",
        subtitle: "Què es planifica, com es planifica i de què està fet",
        hint: "El catàleg del magatzem arriba sencer. Marca només el que de debò es planifica.",
        new: "Nou article",
        newHint: "Un producte o material que no és al magatzem",
        edit: "Editar article",
        item: "Article",
        code: "Codi",
        name: "Nom",
        family: "Família",
        familyHint: "El nivell al qual es planifica i es mira. És la paraula de l\u0027empresa, no una llista nostra.",
        unit: "Unitat",
        kind: "Tipus",
        status: "Al pla",
        inPlan: "Es planifica",
        outOfPlan: "Fora del pla",
        sourcing: "Origen",
        sourcingHint: "Si es fabrica, carrega els centres de treball. Si es compra, va al pla de compres.",
        components: "Estructura",
        component: "Component",
        operations: "{count} operacions",
        stock: "Estoc",
        stockHint: "D\u0027on arrenca la projecció. Arriba del magatzem quan ho publica, o s\u0027escriu aquí.",
        noStock: "Sense estoc conegut",
        missing: "El que falta",
        mirrored: "del magatzem",
        mirroredHint: "El codi, el nom, la unitat, el cost i el proveïdor són del magatzem i s\u0027actualitzen sols. La resta és d\u0027aquí.",
        fromRecipe: "L\u0027escandall es va prendre de la recepta {code} del taller. En editar-lo aquí passa a ser teu: la recepta ja no el trepitja. Un component que no és al catàleg es queda amb el seu nom, sense enllaçar.",
        identity: "Què és",
        policy: "Com es planifica",
        policyHint: "La meitat que no té cap altre mòdul de l\u0027ERP",
        leadTime: "Termini, en dies",
        leadTimeHint: "Des que es decideix fins que hi és. Del proveïdor si es compra, del taller si es fabrica.",
        safetyStock: "Estoc de seguretat",
        safetyStockHint: "El terra que la projecció no baixa mai.",
        lotSize: "Múltiple de comanda",
        lotSizeHint: "S\u0027arrodoneix cap amunt a aquest múltiple. Zero vol dir que no n\u0027hi ha.",
        minimumOrder: "Comanda mínima",
        yield: "Rendiment",
        yieldHint: "Quin percentatge surt bé. Al 96 %, cal començar més del que es necessita.",
        price: "Preu de venda",
        priceHint: "Amb què es calcula la facturació prevista",
        cost: "Cost",
        supplier: "Proveïdor",
        bom: "Escandall de planificació",
        bomHint: "Al nivell al qual es planifica: una màquina porta un motor i un bastidor, no quatre-cents cargols.",
        quantityPerUnit: "Quantitat per unitat",
        scrap: "Minva %",
        pickComponent: "Triar component",
        addComponent: "Afegir component",
        routing: "Ruta",
        routingHint: "Per quins centres passa i quant triga a cadascun. Sense això no hi ha capacitat a calcular.",
        workCenter: "Centre",
        operation: "Operació",
        minutesPerUnit: "Minuts per unitat",
        setupMinutes: "Preparació",
        pickCenter: "Triar centre",
        addOperation: "Afegir operació",
        totalMinutes: "{minutes} minuts per unitat en total",
        plannedField: "Aquest article entra al pla",
        plannedFieldHint: "Només el que està marcat es calcula. Un catàleg sencer no és un pla.",
        selected: "{count} seleccionats",
        addToPlan: "Posar al pla",
        removeFromPlan: "Treure del pla",
        plannedChanged: "{count} articles actualitzats",
        saved: "Article desat",
        searchPlaceholder: "Codi, nom, família…",
        empty: "El catàleg de planificació és buit",
        emptyHint: "Amb el magatzem instal·lat s\u0027omple sol. Sense ell, s\u0027escriu aquí.",
        archiveTitle: "Arxivar l\u0027article",
        archiveMessage: "«{name}» deixa de planificar-se. No s\u0027esborra: els plans signats el continuen anomenant.",
        flags: {
          routing: "Sense ruta",
          bom: "Sense escandall",
          leadTime: "Sense termini",
          family: "Sense família",
        },
      },
      centers: {
        title: "Centres de treball",
        subtitle: "On es fabrica, i quantes hores hi ha de debò",
        hint: "Vuit hores de calendari no són vuit hores de producció: l\u0027eficiència i la disponibilitat són les que manen.",
        new: "Nou centre",
        edit: "Editar centre",
        center: "Centre",
        code: "Codi",
        name: "Nom",
        kind: "Què s\u0027esgota abans",
        kindHint: "Màquina, persones, o totes dues coses. Decideix què li treu una absència.",
        allKinds: "Tots",
        skill: "Competència",
        skillHint: "Una paraula, la de l\u0027empresa: «soldadura certificada». Agrupa centres per llegir una manca com a manca d\u0027alguna cosa.",
        calendar: "Calendari",
        calendarLine: "{hours} h × {shifts} torns × {resources}",
        daysPerWeek: "{days} dies a la setmana",
        hoursPerShift: "Hores per torn",
        shifts: "Torns",
        daysPerWeekField: "Dies a la setmana",
        resources: "Quants alhora",
        resourcesHint: "Quatre CNC, o sis soldadors. Multiplica les hores.",
        factors: "Eficiència i disponibilitat",
        factorsHint: "Dues coses diferents i de dues persones diferents: quant d\u0027una hora a la màquina és feina, i quant temps la màquina està en condicions d\u0027anar.",
        efficiency: "Eficiència %",
        availability: "Disponibilitat %",
        maintenanceHours: "Hores per manteniment",
        maintenanceHoursHint: "El que costa un manteniment preventiu aquí. Manteniment avisa que venç; quantes hores s\u0027emporta només ho sap la planta.",
        money: "Cost de l\u0027hora",
        costPerHour: "Hora normal",
        overtimeCost: "Hora extra",
        subcontractCost: "Hora subcontractada",
        subcontractHours: "Hores que pot absorbir fora",
        subcontractHoursHint: "Zero vol dir que aquesta feina no surt de la planta, que és la resposta habitual.",
        monthlyCapacity: "Capacitat al mes",
        ofCalendar: "de {hours} de calendari",
        links: "Amb què està enganxat",
        crewCount: "{count} persones",
        assetCount: "{count} màquines",
        subcontract: "{hours} h fora",
        preview: "Amb aquests números, un mes normal dona {hours}.",
        saved: "Centre desat",
        searchPlaceholder: "Codi, nom, competència…",
        empty: "Encara no hi ha centres de treball",
        emptyHint: "Són d\u0027aquest mòdul: cap altre lloc de l\u0027ERP sap quantes hores té la planta.",
        archiveTitle: "Arxivar el centre",
        archiveMessage: "«{name}» surt de totes les rutes que l\u0027anomenaven. Si no, aquestes hores desapareixerien del pla sense avisar.",
      },
      form: {
        name: "Nom del pla",
        namePlaceholder: "S&OP 2027",
        nameRequired: "El pla necessita un nom",
        description: "Descripció",
        site: "Planta",
        siteHint: "El centre de treball de l\u0027empresa al qual es refereix aquest pla. En una instància d\u0027una sola planta, es pot deixar buit.",
        sitePlaceholder: "Planta 1",
        siteLinked: "Planta de l\u0027empresa",
        siteUnlink: "Escriure el nom a mà",
        from: "Comença a",
        fromRequired: "Cal un mes d\u0027inici",
        months: "Quants mesos",
        monthsHint: "Dotze és el normal en fabricació. Més, quan els terminis són tan llargs que un any no arriba per decidir res.",
        monthsCount: "{count} mesos",
        granularity: "Es planifica per",
        granularityHint: "Mesos gairebé sempre: això és el pla tàctic, no el programa del taller.",
        currency: "Moneda",
        range: "De {from} a {to}",
        capacityWarn: "Avís de capacitat %",
        capacityWarnHint: "Per sota, el mes és normal. És un avís, no un límit: no es rebutja res.",
        capacityOver: "Sobrecàrrega %",
        capacityCritical: "Crític %",
        frozenWeeks: "Setmanes congelades",
        frozenWeeksHint: "Els mesos de prop, on el material ja està comprat i canviar costa diners.",
        firmMonths: "Mesos ferms",
        usePipeline: "Comptar les oportunitats del CRM",
        usePipelineHint: "Desactivat a propòsit: una empresa que pressuposta tot comptaria la mateixa màquina dues vegades, com a oportunitat i com a pressupost acceptat.",
      },
      wizard: {
        step1: "1 · Quin pla és",
        step1Hint: "El nom amb què se\u0027n parlarà a la reunió",
        step2: "2 · Fins on mira",
        step2Hint: "L\u0027horitzó i la mida del cub",
        step3: "3 · Amb què es jutja",
        step3Hint: "Es copien al pla: una versió aprovada conserva els límits amb què es va signar.",
        step4: "4 · Hi ha alguna cosa a planificar",
        step4Hint: "Un pla sobre un catàleg buit calcula una planta buida, i això s\u0027assembla molt a una resposta.",
        plannedItems: "Articles al pla",
        workCenters: "Centres de treball",
        missingMasters: "Sense articles planificables o sense centres de treball, el càlcul no dirà res útil.",
      },
      demo: {
        created: "Fàbrica d\u0027exemple creada: {items} articles i {centers} centres",
        labels: {
          site: "Planta 1",
          planDescription: "Pla de demostració: fabricació industrial de maquinària",
          extraShift: "Torn extra a muntatge",
          extraShiftHint: "Un segon torn a muntatge durant el mes crític",
          subcontract: "Subcontractar muntatge",
          subcontractHint: "Treure fora part del muntatge, amb el cost que això té",
          demandUp: "Demanda +15 %",
          demandUpHint: "Què passa si l\u0027any va millor del previst",
        },
      },
      dashboard: {
        peak: "Càrrega màxima prevista",
        peakHelp: "La utilització del centre més carregat, en el seu pitjor mes, de tots els plans oberts.",
        risks: "Riscos crítics",
        risksHelp: "Quants riscos crítics continuen oberts als plans que no estan tancats.",
        gap: "Hores que falten",
        gapHelp: "La suma de les hores que els plans oberts demanen per sobre del que hi ha.",
        revenue: "Facturació planificada",
        revenueHelp: "El que sumen les previsions dels plans oberts, als preus del catàleg.",
        riskList: "Els riscos crítics",
        riskListHelp: "Què està en risc, en quin pla i en quin mes. Estira la targeta i n\u0027hi caben més.",
        stale: "Plans per recalcular",
        staleHelp: "Els plans les xifres dels quals ja no corresponen a les dades que tenen a sota.",
      },
    },
    permissions: {
      sop: {
        read: "Veure els plans S&OP",
        demand: "Treballar la demanda: previsió, ajustos i consens",
        supply: "Treballar el subministrament: catàleg, rutes, centres de treball i càlcul",
        scenarios: "Crear i comparar escenaris",
        approve: "Aprovar el pla i apuntar decisions",
        costs: "Veure què val el pla: facturació, cost i marge",
        admin: "Obrir plans, configurar-los i arxivar-los",
      },
    },
    audit: {
      actions: {
        sop: {
          plan_created: "Pla S&OP creat",
          plan_updated: "Pla S&OP modificat",
          plan_stage_changed: "Pla passat a una altra fase del cicle",
          plan_calculated: "Pla S&OP calculat",
          plan_approved: "Pla S&OP aprovat",
          plan_closed: "Pla S&OP tancat",
          plan_archived: "Pla S&OP arxivat",
          plan_restored: "Pla S&OP retornat a les llistes",
          plan_commented: "Comentari al pla",
          plan_comment_removed: "Comentari esborrat del pla",
          demand_edited: "Cel·la de demanda modificada",
          demand_bulk_edited: "Demanda modificada en bloc",
          demand_locked: "Períodes de demanda bloquejats",
          baseline_generated: "Previsió generada de l\u0027històric",
          item_created: "Article de planificació creat",
          item_updated: "Article de planificació modificat",
          item_archived: "Article de planificació arxivat",
          item_restored: "Article retornat al catàleg",
          items_planned: "Articles posats o trets del pla",
          item_stock_set: "Estoc de partida corregit",
          work_center_created: "Centre de treball creat",
          work_center_updated: "Centre de treball modificat",
          work_center_archived: "Centre de treball arxivat",
          work_center_restored: "Centre de treball retornat al catàleg",
          scenario_created: "Escenari creat",
          scenario_updated: "Escenari modificat",
          scenario_archived: "Escenari tret",
          scenario_activated: "Escenari posat a la pantalla",
          scenario_recommended: "Escenari recomanat",
          risk_raised: "Risc apuntat a mà",
          risk_updated: "Risc actualitzat",
          decision_recorded: "Decisió S&OP apuntada",
          decision_updated: "Decisió S&OP actualitzada",
          demo_seeded: "Dades d\u0027exemple creades",
        },
      },
    },
    assistant: {
      openers: {
        sop: {
          risks: "Quins riscos tenim al novembre?",
          bottleneck: "On és el coll d\u0027ampolla del pla?",
          scenarios: "Compara els escenaris del pla actual",
        },
      },
    },
    settings: {
      ns: {
        sop: {
          title: "Planificació S&OP",
          subtitle: "Com planifica aquesta empresa: horitzó, llindars i què compta com a demanda",
          defaultHorizonMonths: {
            label: "Mesos que mira un pla nou",
            help: "Dotze és la resposta habitual en fabricació. Divuit o vint-i-quatre si els terminis són tan llargs que un any no arriba per decidir res.",
          },
          defaultGranularity: {
            label: "El cub d\u0027un pla nou",
            help: "Mesos gairebé sempre: això és el pla tàctic, no el programa del taller. Setmanes només on l\u0027horitzó és curt i les decisions són així de fines.",
          },
          currency: {
            label: "Moneda en què es valoren els plans",
            help: "Codi ISO. És en què s\u0027expressen la facturació, el cost i el marge d\u0027un pla.",
          },
          capacityWarnPct: {
            label: "Utilització a partir de la qual vigilar un centre",
            help: "Per sota, el període és normal. És un avís i no un límit: no es rebutja res.",
          },
          capacityOverPct: {
            label: "Utilització a partir de la qual un centre no hi cap",
            help: "Cent vol dir que el pla demana més hores de les que té la planta. Algunes plantes posen la seva referència per sobre a propòsit, perquè els seus temps estàndard són conservadors.",
          },
          capacityCriticalPct: {
            label: "Utilització a partir de la qual és un risc crític",
            help: "Passat això, el pla aixeca un risc crític i el bus el porta. Posa\u0027l on les hores extra i una mica de subcontractació deixen de ser suficients.",
          },
          coverageWarnDays: {
            label: "Dies de cobertura per sota dels quals avisar",
            help: "Es mesura contra el consum que VE DESPRÉS del saldo, mai contra el passat. Un material sense demanda per davant no té cobertura ni avís.",
          },
          forecastBiasWarnPct: {
            label: "Desviació sistemàtica que ja és un problema",
            help: "El biaix és l\u0027error mitjà AMB SIGNE: una previsió equivocada un 15 % en la mateixa direcció mes rere mes és un procés trencat, no mala sort.",
          },
          serviceTargetPct: {
            label: "Nivell de servei al qual apunta l\u0027empresa",
            help: "Contra el que es compara el pla. La xifra que produeix és una ESTIMACIÓ a partir del mateix pla: la mesura surt dels lliuraments, que aquest mòdul no veu.",
          },
          frozenWeeks: {
            label: "Setmanes d\u0027horitzó congelat",
            help: "Els períodes de prop, on el material ja està comprat i canviar costa diners. Acoloreix la graella; bloquejar una cel·la continua sent una decisió que pren algú.",
          },
          firmMonths: {
            label: "Mesos d\u0027horitzó ferm després del congelat",
            help: "Aquí els canvis són controlats, no impossibles. Tot el que ve després és flexible.",
          },
          usePipeline: {
            label: "Comptar les oportunitats del CRM a la demanda",
            help: "Desactivat a propòsit: una empresa que pressuposta tot comptaria la mateixa màquina dues vegades, com a oportunitat i com el pressupost acceptat en què es va convertir. Activa\u0027l només on les oportunitats no acaben en pressupost.",
          },
        },
      },
    },
  },
  en: {
    sop: {
      title: "S&OP planning",
      search: {
        subtitle: "Tactical plan",
      },
      notifications: {
        categories: {
          planning: "S&OP planning",
        },
      },
      units: {
        days: "{count} days",
      },
      actions: {
        newPlan: "New plan",
        createPlan: "Create the plan",
        calculate: "Calculate",
        approve: "Approve",
        handTo: "Hand over to {stage}",
        save: "Save",
        cancel: "Cancel",
        apply: "Apply",
        remove: "Remove",
        archive: "Archive",
        openCatalogue: "Open the catalogue",
        openWorkCenters: "Open work centres",
        seedDemo: "Create demo data",
      },
      pager: {
        previous: "Previous",
        next: "Next",
        of: "Page {page} of {pages}",
      },
      empty: {
        none: "There is no S&OP plan yet",
        noneHint: "Create the first one to look at demand, capacity and inventory for the months ahead.",
        filtered: "No results",
        filteredHint: "Try removing a filter or searching for something else.",
      },
      status: {
        draft: "Draft",
        preparation: "Preparation",
        demand_review: "Demand review",
        supply_review: "Supply review",
        capacity_review: "Capacity review",
        scenario_review: "Scenario review",
        financial_review: "Financial review",
        executive_review: "Executive meeting",
        approved: "Approved",
        closed: "Closed",
      },
      cycle: {
        title: "S&OP cycle",
        moved: "The plan moves to {stage}",
      },
      calc: {
        never: "Not calculated",
        stale: "Out of date",
        running: "Calculating",
        ready: "Up to date",
        failed: "Calculation failed",
        lastRun: "Last calculated: {when}",
        done: "Plan calculated in {ms} ms · {risks} risks",
        notice: {
          never: "This plan has not been calculated yet: the screens stay empty until it is.",
          stale: "Something has changed since the last run. The figures on screen are the old ones.",
          running: "The plan is being calculated.",
          ready: "The figures are up to date.",
          failed: "The last calculation failed. Check the data readiness and try again.",
        },
      },
      granularity: {
        month: "Month",
        week: "Week",
        quarter: "Quarter",
      },
      fence: {
        frozen: "frozen",
        firm: "firm",
        flexible: "flexible",
      },
      plans: {
        title: "Plans",
        subtitle: "The company\u0027s S&OP plans, and where each one is in its cycle",
        detailTitle: "S&OP plan",
        newTitle: "New S&OP plan",
        newSubtitle: "What is planned, how far ahead, and against what limits",
        versionLabel: "v{version}",
        created: "Plan {code} created",
      },
      table: {
        code: "Code",
        name: "Plan",
        horizon: "Horizon",
        status: "Stage",
        calc: "Calculation",
        version: "Version",
      },
      tabs: {
        overview: "Overview",
        demand: "Demand",
        supply: "Supply",
        capacity: "Capacity",
        inventory: "Inventory",
        procurement: "Purchasing",
        scenarios: "Scenarios",
        risks: "Risks",
        versions: "Approved plan",
        actual: "Follow-up",
      },
      filters: {
        search: "Search",
        searchPlaceholder: "Plan code or name…",
        status: "Stage",
        allStatuses: "All",
        family: "Family",
        allFamilies: "All families",
        sourcing: "Source",
        allSourcings: "Made and bought",
        planned: "In the plan",
        allItems: "The whole catalogue",
        state: "State",
        allStates: "All states",
        risk: "Risk",
        allRisks: "All",
        severity: "Severity",
        allSeverities: "All",
        riskType: "Type",
        allTypes: "All types",
      },
      kpi: {
        openPlans: "Open plans",
        approvedPlans: "Approved plans",
        closedPlans: "Closed plans",
        needsCalc: "Waiting to be calculated",
        demand: "Expected demand",
        confirmed: "Already confirmed",
        peakUtilisation: "Peak utilisation",
        capacityGap: "Hours missing",
        stockouts: "Materials running out",
        service: "Estimated service level",
        purchaseRisk: "Purchases at risk",
        revenue: "Expected revenue",
        cost: "Expected cost",
        margin: "Expected margin",
        inventoryValue: "Peak inventory value",
      },
      cockpit: {
        title: "S&OP summary",
        subtitle: "How the plan stands, and what has to be decided",
        pick: "Plan",
        openPlan: "Open the plan",
        decisions: "Decisions to take",
        decisionsHint: "What is already written down first, then the critical risks nobody has decided on",
        noDecisions: "Nothing waiting on a decision",
        criticalRisks: "Critical risks",
        criticalRisksHint: "The ones that do not fix themselves",
        demandLine: "{confirmed} is already confirmed",
        capacityGap: "{hours} missing across {centers} centres",
        capacityOk: "The plant can do it",
        inventoryLine: "{below} below safety stock",
        supplyLine: "of {lines} purchase lines",
        serviceLine: "The plan\u0027s estimate, not measured on deliveries",
        financeLine: "Cost {cost} · margin {margin}",
        riskWhy: "Critical risk in {period}",
        riskWhyNoPeriod: "Critical risk on the plan",
        areas: {
          demand: "Demand",
          capacity: "Capacity",
          inventory: "Inventory",
          supply: "Supply",
          service: "Service",
          finance: "Finance",
        },
      },
      overview: {
        notCalculated: "The plan has not been calculated yet",
        notCalculatedHint: "Run it to see demand, capacity, inventory and risks.",
        criticalMonth: "{center} does not fit in {period}",
        criticalLine: "It is at {utilisation}: {required} are needed and there are {available}.",
        bottlenecks: "Bottlenecks",
        bottlenecksHint: "Ranked by how far over they are, not by how many hours",
        noBottlenecks: "No centre is over capacity",
        alerts: "Alerts",
        alertsHint: "What does not fit, with what could be done about it",
        noRisks: "No open risks",
        optionHint: "\u0027{option}\u0027 is decided on this screen",
      },
      charts: {
        demandVsCapacity: "Demand against capacity",
        demandVsCapacityHint: "The bars are the hours the plan asks for; the line is what the plant has",
        requiredHours: "Hours needed",
        availableHours: "Hours available",
        sales: "Sales over time",
        salesHint: "What is already ordered, and what is forecast",
        confirmed: "Confirmed orders",
        forecast: "Forecast",
        stock: "Projected stock",
        stockHint: "How the warehouse ends each month",
        projectedStock: "Projected stock",
      },
      readiness: {
        title: "Data readiness",
        subtitle: "What the plan could not read. A calculation over incomplete data does not fail: it gives a wrong answer.",
        ready: "of the data it needs",
        count: "{ready} of {total}",
        feeds: "What arrives from other modules",
        feedRows: "{count} records · last {when}",
        feedEmpty: "Nothing yet",
        checks: {
          routing: "Products with a routing",
          bom: "Products with a structure",
          leadTime: "Materials with a lead time",
          cost: "Items with a cost",
          price: "Products with a price",
          family: "Items with a family",
          stock: "Items with a known stock",
          supplier: "Materials with a supplier",
          calendar: "Centres with a calendar",
        },
      },
      feeds: {
        sales_history: "Sales history",
        order_book: "Order book",
        open_production: "Open manufacturing orders",
        open_purchase: "Open purchase orders",
        production_actual: "Actual production",
        purchase_actual: "Goods received",
        stock_level: "Stock levels",
        absence: "Approved absences",
        maintenance: "Planned maintenance",
        asset_stop: "Stopped machines",
      },
      quality: {
        itemsWithoutRouting: "Products with no routing",
        itemsWithoutBom: "Products with no structure",
        itemsWithoutLeadTime: "Materials with no lead time",
        itemsWithoutCost: "Items with no cost",
        itemsWithoutStock: "Items with no known stock",
        workCentersWithoutCalendar: "Centres with no calendar",
      },
      demand: {
        item: "Item",
        total: "Total",
        consensusTotal: "S&OP demand",
        showing: "Showing",
        truncated: "There are more items than fit. Filter by family to see them.",
        empty: "There is nothing to plan",
        emptyHint: "Mark items in the catalogue as planned so they appear here.",
        generate: "Generate a forecast",
        generated: "Forecast generated · {items} of {items} items had history",
        bulk: "Bulk edit",
        bulkTitle: "Bulk edit",
        bulkOperation: "What to do",
        bulkValue: "Value",
        bulkField: "On what",
        bulkHint: "It applies to what is on screen, and never to a locked period.",
        bulkDone: "{changed} cells changed",
        bulkDoneSkipped: "{changed} cells changed · {skipped} locked ones left alone",
        fields: {
          consensus: "Consensus demand",
          confirmed: "Confirmed orders",
          baseline: "Forecast",
          pipeline: "CRM pipeline",
          adjustment: "Sales adjustment",
          override: "Decided figure",
        },
        hints: {
          consensus: "The sum of everything else, or the decided figure when there is one. It is what the rest of the module plans against.",
          confirmed: "It comes from accepted quotes. It is not typed here.",
          baseline: "What the history says, or what you type over it.",
          pipeline: "CRM opportunities weighted by their probability. It is not typed here.",
          adjustment: "What sales know that none of the figures above does.",
          override: "It replaces the sum rather than adding to it. Empty means there is none.",
        },
        operations: {
          increase: "Raise by a percentage",
          set: "Set a fixed value",
          distribute: "Spread a total",
          clear: "Clear",
        },
      },
      sourcing: {
        make: "Made",
        buy: "Bought",
      },
      itemKind: {
        finished: "Finished product",
        component: "Component",
        raw: "Raw material",
        service: "Service",
      },
      centerKind: {
        machine: "Machine",
        labour: "People",
        mixed: "Mixed",
      },
      load: {
        idle: "Idle",
        normal: "Normal",
        warning: "Tight",
        over: "Over capacity",
        critical: "Critical",
      },
      stock: {
        stockout: "Stockout",
        below_safety: "Below safety stock",
        low: "Tight",
        healthy: "Healthy",
        excess: "Excess",
      },
      supply: {
        showing: "Showing",
        level: "level {level}",
        empty: "There is nothing to supply",
        emptyHint: "Run it with demand on it and this shows what has to be made and bought.",
        problems: "Problems in the data",
        problemsHint: "Faults in a structure or a routing, not risks of the plan",
        fields: {
          start: "Has to be started",
          planned: "Has to arrive",
          gross: "Gross requirement",
          independent: "Own demand",
          dependent: "Demand from its parents",
          scheduled: "Already coming",
          projected: "Resulting stock",
        },
        hints: {
          start: "The period the decision has to be taken in, with the lead time already taken off. It is the row you act on.",
          planned: "The period it needs to be there.",
          gross: "What the whole plan consumes: what is sold and what goes inside something else.",
          independent: "What is sold of this directly.",
          dependent: "What goes inside the products that use it.",
          scheduled: "Manufacturing orders and purchase orders already open.",
          projected: "How the warehouse ends each period.",
        },
      },
      problems: {
        BOM_CYCLE: "Circular structure",
        BOM_ITEM_MISSING: "Component not in the catalogue",
        BOM_TOO_DEEP: "Structure too deep",
        ROUTING_MISSING: "Product with no routing",
        WORK_CENTER_MISSING: "Routing pointing at a centre that no longer exists",
      },
      capacity: {
        center: "Work centre",
        heatmap: "Load by centre and month",
        heatmapHint: "Click a cell to see what is loading it",
        detail: "Work centres",
        detailHint: "What the plan asks against what there is, over the whole horizon",
        available: "Available",
        required: "Needed",
        gap: "Gap",
        peak: "Peak",
        utilisation: "Utilisation",
        calendar: "Calendar",
        calendarBreakdown: "Where the hours come from",
        downtime: "Maintenance and stops",
        absence: "Absences",
        scenarioHours: "Scenario hours",
        whatLoadsIt: "What is loading it",
        nothingLoads: "Nothing loads it in this period",
        empty: "There are no work centres",
        emptyHint: "With no centres there is no capacity to calculate: the plant comes out empty.",
      },
      inventory: {
        showing: "Showing",
        worst: "Worst month",
        safety: "safety {quantity}",
        value: "Value",
        valueChart: "Inventory value",
        valueChartHint: "What has to be financed, month by month",
        empty: "There is no inventory to project",
        emptyHint: "Run it with demand and a catalogue.",
        excess: "Excess and obsolete",
        excessHint: "What the plan will not consume",
        excessType: "Type",
        quantity: "Quantity",
        cover: "Cover",
        horizons: "{count} times the horizon",
        excessKind: {
          dead: "No movement",
          excess: "Excess",
        },
        fields: {
          closing: "Closing stock",
          demand: "Consumption",
          receipts: "Expected receipts",
          production: "Planned replenishment",
          coverageDays: "Days of cover",
          value: "Value",
        },
      },
      procurement: {
        hint: "Sorted by the latest order date, not by value: what is urgent is what no longer arrives in time.",
        need: "To buy",
        ofTotal: "of {total} needed in total",
        stock: "Stock",
        onOrder: "{quantity} already on order",
        leadTime: "Lead time",
        orderBy: "Order by",
        risk: "Risk",
        cost: "Amount",
        noSupplier: "no supplier",
        noLeadTime: "No lead time",
        unplaceable: "{count} periods that can no longer be ordered",
        spend: "Purchase commitment",
        spendHint: "What has to be committed each month, not what arrives",
        empty: "There is nothing to buy",
        emptyHint: "With the current demand and stock, the plan covers itself.",
        risks: {
          none: "In time",
          tight: "Tight",
          late: "Late",
        },
      },
      scenarios: {
        base: "Base plan",
        new: "New scenario",
        manage: "Scenarios",
        intro: "A scenario is a question, not a copy of the plan: a few levers over the same data.",
        metric: "Metric",
        name: "Name",
        description: "Description",
        template: "Starting point",
        templateHint: "Fills in the levers that question usually needs. They can be changed.",
        levers: "Levers",
        lever: "Lever",
        leverCount: "Levers",
        addLever: "Add a lever",
        noLevers: "No levers: this is the plan as it stands",
        pickCenter: "Pick a centre",
        pickItem: "Pick an item",
        value: "Value",
        from: "From",
        to: "To",
        fromAny: "The whole horizon",
        toAny: "The whole horizon",
        active: "On screen",
        recommended: "Recommended",
        approved: "Approved",
        read: "Read with this one",
        reading: "The plan is read through this scenario",
        recommend: "Recommend",
        recommendedDone: "\u0027{name}\u0027 is now the recommended scenario",
        created: "Scenario created. Calculate it to see its figures.",
        removeTitle: "Remove the scenario",
        removeMessage: "\u0027{name}\u0027 and its figures go. The plan itself is untouched.",
      },
      templates: {
        base: "Base plan",
        demand_up: "Demand goes up",
        demand_down: "Demand falls",
        extra_shift: "One more shift",
        hire: "Hire",
        subcontract: "Subcontract",
        new_machine: "A new machine",
        lead_time_up: "Lead times get longer",
        machine_down: "A machine stops",
        cost_up: "Costs go up",
      },
      levers: {
        everything: "everything",
        between: "from {from} to {to}",
        demand_pct: "Demand",
        demand_delta: "Demand, in units",
        capacity_shifts: "Shifts",
        capacity_resources: "Machines or people",
        capacity_hours: "Extra hours",
        capacity_efficiency: "Centre efficiency",
        capacity_subcontract: "Subcontractable hours",
        capacity_offline: "Hours lost",
        lead_time_days: "Lead time",
        purchase_cost_pct: "Purchase cost",
        minimum_order: "Minimum order",
        routing_efficiency: "Routing efficiency",
        sourcing: "Make or buy",
        safety_stock: "Safety stock",
        safety_stock_pct: "Safety stock, as a percentage",
        opening_stock: "Opening stock",
      },
      metrics: {
        revenue: "Revenue",
        cost: "Cost",
        marginPct: "Margin",
        serviceLevelEstimate: "Service level",
        peakUtilisation: "Peak load",
        capacityGapHours: "Hours missing",
        stockoutItems: "Materials running out",
        peakInventoryValue: "Stock investment",
        criticalRisks: "Critical risks",
        good: {
          up: "higher is better",
          down: "lower is better",
        },
      },
      severity: {
        low: "Low",
        medium: "Medium",
        high: "High",
        critical: "Critical",
      },
      riskStatus: {
        open: "Open",
        accepted: "Accepted",
        mitigating: "Being worked on",
        resolved: "Resolved",
        dismissed: "Dismissed",
      },
      riskType: {
        capacity: "Capacity",
        stock: "Stockout",
        material: "Material",
        supplier: "Supplier",
        lead_time: "Lead time",
        demand: "Demand",
        machine: "Machine",
        personnel: "People",
        skill: "Skills",
        quality: "Quality",
        financial: "Financial",
        logistic: "Logistics",
        data: "Data",
      },
      risks: {
        options: "What could be done",
        status: "Risk status",
        critical: "Critical",
        high: "High",
        medium: "Medium",
        open: "Open",
        empty: "No open risks",
        emptyHint: "The plan fits as it stands.",
        noSupplier: "no supplier set",
        sentences: {
          capacity: "In {period} it is at {utilisation}: {required} are needed and there are {available}. {gap} short.",
          stock: "In {period} it ends at {closing} against a minimum of {safety}. {shortfall} short.",
          coverage: "Only {days} days of cover are left, and the warning is at {threshold}.",
          leadTime: "It no longer arrives in time for {period}: {days} days of lead time with {supplier}, and {quantity} are needed.",
          supplier: "For {period} it had to be ordered by {orderBy}, and {slack} days have passed. Supplier: {supplier}.",
          jump: "The forecast jumps {jump} over recent months: from {average} to {peak}.",
          bias: "The forecast drifts the same way every month: {bias}, and the warning is at {threshold}.",
          data: "{count} of {of} items are missing this, so the calculation does not count them properly.",
        },
      },
      options: {
        add_shift: "Add a shift",
        subcontract: "Subcontract",
        hire: "Hire",
        move_earlier: "Move the work earlier",
        order_now: "Order it now",
        bring_purchase_forward: "Bring the purchase forward",
        raise_safety_stock: "Raise the safety stock",
        alternative_supplier: "Find another supplier",
        split_delivery: "Split the delivery",
        confirm_forecast: "Confirm the forecast",
        review_forecast_process: "Review how the forecast is made",
        complete_routings: "Complete the routings",
        complete_boms: "Complete the structures",
        complete_lead_times: "Complete the lead times",
        complete_costs: "Complete the costs",
        import_stock: "Load the stock",
        complete_calendars: "Complete the calendars",
      },
      decisions: {
        title: "Decisions",
        subtitle: "What was decided, and why. It is what explains the plan six months later.",
        record: "Record a decision",
        recordFor: "Record a decision",
        recorded: "Decision recorded",
        what: "What is decided",
        whatPlaceholder: "Second shift in assembly, weeks 45-48",
        why: "Why",
        whyHint: "Required: a decision with no reason explains nothing six months from now.",
        impact: "What it should change",
        impactHint: "In words: \u0027+160 h\u0027. Half of these decisions have no figure.",
        hoursImpact: "+{hours} h",
        period: "Period",
        anyPeriod: "The whole plan",
        status: "Status",
        owner: "Owner",
        when: "When",
        empty: "No decisions recorded yet",
        emptyHint: "Every change to the plan worth explaining fits here in two lines.",
        statuses: {
          proposed: "Proposed",
          approved: "Approved",
          rejected: "Rejected",
          done: "Done",
        },
      },
      approve: {
        title: "Approve the plan",
        message: "A version is frozen with the figures as they stand. It cannot be edited, and any later change is a new version.",
        messageScenario: "The \u0027{scenario}\u0027 scenario is approved and a version is frozen with its figures. It cannot be edited.",
        done: "Plan approved · version {version}",
      },
      versions: {
        version: "Version",
        scenario: "Scenario",
        approvedAt: "Approved",
        approvedBy: "By",
        comment: "Comment",
        compare: "Compare versions",
        compareHint: "What changed between two signatures",
        first: "Earlier version",
        second: "Later version",
        frozen: "This version is frozen: it reads today exactly as it did the day it was signed.",
        risksAtApproval: "Risks open at approval",
        empty: "No version has been approved yet",
        emptyHint: "When management signs the plan, the version is frozen here.",
      },
      actual: {
        against: "Compare against",
        hint: "Against version {version}, signed on {when} by {who}.",
        period: "Period",
        sales: "Sales",
        production: "Production",
        hours: "Hours",
        purchases: "Purchases",
        utilisation: "Utilisation",
        salesVariance: "Sales variance",
        productionVariance: "Production variance",
        hoursVariance: "Hours variance",
        unavailable: "There is nothing to compare yet",
        unavailableHint: "The follow-up compares against a signed version, and this plan has none.",
        tooEarly: "No period has happened yet",
        tooEarlyHint: "Only what has already happened is compared, never what is still ahead.",
        feeds: "Where the real figures come from",
        feedsHint: "What is not installed shows a dash, never a zero",
        feedOn: "Arriving",
        feedOff: "Not installed",
        feedNames: {
          sales: "Invoicing",
          production: "Production",
          purchases: "Purchasing",
        },
      },
      items: {
        title: "Planning items",
        subtitle: "What is planned, how it is planned and what it is made of",
        hint: "The whole warehouse catalogue arrives. Mark only what is really planned.",
        new: "New item",
        newHint: "A product or material the warehouse does not hold",
        edit: "Edit item",
        item: "Item",
        code: "Code",
        name: "Name",
        family: "Family",
        familyHint: "The level things are planned and read at. It is the company\u0027s own word, not a list of ours.",
        unit: "Unit",
        kind: "Kind",
        status: "In the plan",
        inPlan: "Planned",
        outOfPlan: "Not planned",
        sourcing: "Source",
        sourcingHint: "If it is made, it loads the work centres. If it is bought, it goes to the purchase plan.",
        components: "Structure",
        component: "Component",
        operations: "{count} operations",
        stock: "Stock",
        stockHint: "Where the projection starts from. It arrives from the warehouse when it publishes one, or is typed here.",
        noStock: "No known stock",
        missing: "What is missing",
        mirrored: "from the warehouse",
        mirroredHint: "The code, name, unit, cost and supplier belong to the warehouse and update themselves. The rest is this module\u0027s.",
        fromRecipe: "The structure was taken from the shop\u0027s recipe {code}. Editing it here makes it yours: the recipe will not overwrite it again. A component that is not in the catalogue keeps its name, unlinked.",
        identity: "What it is",
        policy: "How it is planned",
        policyHint: "The half no other module of the ERP has",
        leadTime: "Lead time, in days",
        leadTimeHint: "From deciding to having it. The supplier\u0027s if bought, the shop\u0027s if made.",
        safetyStock: "Safety stock",
        safetyStockHint: "The floor the projection never goes under.",
        lotSize: "Lot size",
        lotSizeHint: "Quantities are rounded up to this multiple. Zero means there is none.",
        minimumOrder: "Minimum order",
        yield: "Yield",
        yieldHint: "What share comes out good. At 96 %, more has to be started than is needed.",
        price: "Sale price",
        priceHint: "What the expected revenue is worked out from",
        cost: "Cost",
        supplier: "Supplier",
        bom: "Planning structure",
        bomHint: "At the level things are planned: a machine has a motor and a frame, not four hundred screws.",
        quantityPerUnit: "Quantity per unit",
        scrap: "Scrap %",
        pickComponent: "Pick a component",
        addComponent: "Add a component",
        routing: "Routing",
        routingHint: "Which centres it goes through and how long it takes in each. Without this there is no capacity to calculate.",
        workCenter: "Centre",
        operation: "Operation",
        minutesPerUnit: "Minutes per unit",
        setupMinutes: "Setup",
        pickCenter: "Pick a centre",
        addOperation: "Add an operation",
        totalMinutes: "{minutes} minutes per unit in total",
        plannedField: "This item is part of the plan",
        plannedFieldHint: "Only what is marked is calculated. A whole catalogue is not a plan.",
        selected: "{count} selected",
        addToPlan: "Add to the plan",
        removeFromPlan: "Take out of the plan",
        plannedChanged: "{count} items updated",
        saved: "Item saved",
        searchPlaceholder: "Code, name, family…",
        empty: "The planning catalogue is empty",
        emptyHint: "With the warehouse installed it fills itself. Without it, it is typed here.",
        archiveTitle: "Archive the item",
        archiveMessage: "\u0027{name}\u0027 stops being planned. It is not deleted: signed plans still name it.",
        flags: {
          routing: "No routing",
          bom: "No structure",
          leadTime: "No lead time",
          family: "No family",
        },
      },
      centers: {
        title: "Work centres",
        subtitle: "Where things are made, and how many hours there really are",
        hint: "Eight calendar hours are not eight productive hours: efficiency and availability are what decide.",
        new: "New centre",
        edit: "Edit centre",
        center: "Centre",
        code: "Code",
        name: "Name",
        kind: "What runs out first",
        kindHint: "A machine, people, or both. It decides what an absence takes away from it.",
        allKinds: "All",
        skill: "Skill",
        skillHint: "One word, the company\u0027s own: \u0027certified welding\u0027. It groups centres so a shortage reads as a shortage OF something.",
        calendar: "Calendar",
        calendarLine: "{hours} h × {shifts} shifts × {resources}",
        daysPerWeek: "{days} days a week",
        hoursPerShift: "Hours per shift",
        shifts: "Shifts",
        daysPerWeekField: "Days a week",
        resources: "How many at once",
        resourcesHint: "Four CNCs, or six welders. It multiplies the hours.",
        factors: "Efficiency and availability",
        factorsHint: "Two different facts from two different people: how much of an hour at the machine is work, and how much of the time the machine is fit to run.",
        efficiency: "Efficiency %",
        availability: "Availability %",
        maintenanceHours: "Hours per maintenance",
        maintenanceHoursHint: "What one preventive maintenance costs here. Maintenance says one is due; how many hours it eats is only known in the plant.",
        money: "Cost of an hour",
        costPerHour: "Ordinary hour",
        overtimeCost: "Overtime hour",
        subcontractCost: "Subcontracted hour",
        subcontractHours: "Hours a subcontractor could absorb",
        subcontractHoursHint: "Zero means this work does not leave the plant, which is the usual answer.",
        monthlyCapacity: "Monthly capacity",
        ofCalendar: "of {hours} of calendar",
        links: "What it is wired to",
        crewCount: "{count} people",
        assetCount: "{count} machines",
        subcontract: "{hours} h outside",
        preview: "With these numbers, an ordinary month gives {hours}.",
        saved: "Centre saved",
        searchPlaceholder: "Code, name, skill…",
        empty: "There are no work centres yet",
        emptyHint: "They belong to this module: nowhere else in the ERP knows how many hours the plant has.",
        archiveTitle: "Archive the centre",
        archiveMessage: "\u0027{name}\u0027 comes off every routing that named it. Otherwise those hours would vanish from the plan with no warning.",
      },
      form: {
        name: "Plan name",
        namePlaceholder: "S&OP 2027",
        nameRequired: "The plan needs a name",
        description: "Description",
        site: "Plant",
        siteHint: "The company site this plan is about. On a single-site instance it can be left empty.",
        sitePlaceholder: "Plant 1",
        siteLinked: "A company site",
        siteUnlink: "Type the name instead",
        from: "Starts in",
        fromRequired: "A starting month is needed",
        months: "How many months",
        monthsHint: "Twelve is usual in manufacturing. More, when lead times are long enough that a year is not far enough to decide anything.",
        monthsCount: "{count} months",
        granularity: "Planned in",
        granularityHint: "Months almost always: this is the tactical plan, not the shop schedule.",
        currency: "Currency",
        range: "From {from} to {to}",
        capacityWarn: "Capacity warning %",
        capacityWarnHint: "Below this, the month is normal. It is a warning, not a limit: nothing is refused.",
        capacityOver: "Over capacity %",
        capacityCritical: "Critical %",
        frozenWeeks: "Frozen weeks",
        frozenWeeksHint: "The near months, where material is already bought and changes cost money.",
        firmMonths: "Firm months",
        usePipeline: "Count CRM opportunities",
        usePipelineHint: "Off on purpose: a company that quotes everything would count the same machine twice, as an opportunity and as the accepted quote it became.",
      },
      wizard: {
        step1: "1 · Which plan this is",
        step1Hint: "The name it will be called by in the meeting",
        step2: "2 · How far it looks",
        step2Hint: "The horizon and the size of the bucket",
        step3: "3 · What it is judged by",
        step3Hint: "They are copied onto the plan: an approved version keeps the limits it was signed under.",
        step4: "4 · Is there anything to plan",
        step4Hint: "A plan over an empty catalogue calculates an empty plant, and that looks a lot like an answer.",
        plannedItems: "Items in the plan",
        workCenters: "Work centres",
        missingMasters: "With no planning items or no work centres, the calculation will say nothing useful.",
      },
      demo: {
        created: "Demo factory created: {items} items and {centers} centres",
        labels: {
          site: "Plant 1",
          planDescription: "Demonstration plan: industrial machinery manufacturing",
          extraShift: "Extra shift in assembly",
          extraShiftHint: "A second shift in assembly during the critical month",
          subcontract: "Subcontract assembly",
          subcontractHint: "Send part of the assembly out, with what that costs",
          demandUp: "Demand +15 %",
          demandUpHint: "What happens if the year goes better than expected",
        },
      },
      dashboard: {
        peak: "Peak planned load",
        peakHelp: "The utilisation of the busiest work centre, in its worst month, across every open plan.",
        risks: "Critical risks",
        risksHelp: "How many critical risks are still open on plans that are not closed.",
        gap: "Hours missing",
        gapHelp: "The total hours the open plans ask for beyond what the plant has.",
        revenue: "Planned revenue",
        revenueHelp: "What the forecasts of the open plans add up to, at catalogue prices.",
        riskList: "The critical risks",
        riskListHelp: "What is at risk, in which plan and in which month. Stretch the card and more fit.",
        stale: "Plans to recalculate",
        staleHelp: "The plans whose figures no longer match the data underneath them.",
      },
    },
    permissions: {
      sop: {
        read: "See the S&OP plans",
        demand: "Work the demand: forecast, adjustments and consensus",
        supply: "Work the supply: catalogue, routings, work centres and the calculation",
        scenarios: "Build and compare scenarios",
        approve: "Approve the plan and record decisions",
        costs: "See what the plan is worth: revenue, cost and margin",
        admin: "Open plans, configure them and archive them",
      },
    },
    audit: {
      actions: {
        sop: {
          plan_created: "S&OP plan created",
          plan_updated: "S&OP plan changed",
          plan_stage_changed: "Plan handed to another stage of the cycle",
          plan_calculated: "S&OP plan calculated",
          plan_approved: "S&OP plan approved",
          plan_closed: "S&OP plan closed",
          plan_archived: "S&OP plan archived",
          plan_restored: "S&OP plan brought back",
          plan_commented: "Comment on the plan",
          plan_comment_removed: "Comment removed from the plan",
          demand_edited: "Demand cell changed",
          demand_bulk_edited: "Demand changed in bulk",
          demand_locked: "Demand periods locked",
          baseline_generated: "Forecast generated from history",
          item_created: "Planning item created",
          item_updated: "Planning item changed",
          item_archived: "Planning item archived",
          item_restored: "Planning item brought back",
          items_planned: "Items added to or taken out of the plan",
          item_stock_set: "Opening stock corrected",
          work_center_created: "Work centre created",
          work_center_updated: "Work centre changed",
          work_center_archived: "Work centre archived",
          work_center_restored: "Work centre brought back",
          scenario_created: "Scenario created",
          scenario_updated: "Scenario changed",
          scenario_archived: "Scenario removed",
          scenario_activated: "Scenario put on screen",
          scenario_recommended: "Scenario recommended",
          risk_raised: "Risk raised by hand",
          risk_updated: "Risk updated",
          decision_recorded: "S&OP decision recorded",
          decision_updated: "S&OP decision updated",
          demo_seeded: "Demo data created",
        },
      },
    },
    assistant: {
      openers: {
        sop: {
          risks: "What risks do we have next November?",
          bottleneck: "Where is the bottleneck in the plan?",
          scenarios: "Compare the scenarios of the current plan",
        },
      },
    },
    settings: {
      ns: {
        sop: {
          title: "S&OP planning",
          subtitle: "How this company plans: horizon, thresholds and what counts as demand",
          defaultHorizonMonths: {
            label: "How many months a new plan looks ahead",
            help: "Twelve is the usual answer for a manufacturer. Eighteen or twenty-four if the lead times are long enough that a year is not far enough to decide anything.",
          },
          defaultGranularity: {
            label: "The bucket a new plan is made in",
            help: "Months, almost always: this is the tactical plan, not the shop schedule. Weeks only where the whole horizon is short and the decisions are that fine.",
          },
          currency: {
            label: "The currency plans are valued in",
            help: "ISO code. It is what the revenue, cost and margin of a plan are expressed in.",
          },
          capacityWarnPct: {
            label: "Utilisation at which a work centre is worth watching",
            help: "Below this a period is normal. It is a warning, not a limit: nothing is refused.",
          },
          capacityOverPct: {
            label: "Utilisation above which a work centre does not fit",
            help: "A hundred means the plan needs more hours than the plant has. Some plants set their reference above it on purpose, because their standard times are conservative.",
          },
          capacityCriticalPct: {
            label: "Utilisation at which a period is a critical risk",
            help: "Past this, the plan raises a critical risk and the bus carries it. Set it where overtime and a bit of subcontracting stop being enough.",
          },
          coverageWarnDays: {
            label: "Days of stock cover below which a material is a warning",
            help: "Measured against the demand that FOLLOWS the balance, never against past consumption. A part with no demand ahead has no cover figure and no warning.",
          },
          forecastBiasWarnPct: {
            label: "Forecast bias that is worth calling a problem",
            help: "Bias is the average SIGNED error: a forecast that is wrong by 15 % in the same direction month after month is a broken process, not bad luck.",
          },
          serviceTargetPct: {
            label: "The service level the company aims at",
            help: "What the plan is compared against. The figure it produces is an ESTIMATE from the plan itself — the measured one comes from deliveries, which this module does not see.",
          },
          frozenWeeks: {
            label: "Weeks of frozen horizon",
            help: "The near periods, where material is bought and changes cost money. It colours the grid; locking a cell is still a decision somebody takes.",
          },
          firmMonths: {
            label: "Months of firm horizon after the frozen one",
            help: "Changes here are controlled rather than impossible. Everything past it is flexible.",
          },
          usePipeline: {
            label: "Count weighted CRM opportunities in the demand",
            help: "Off by default, and deliberately: a company that quotes everything would count the same machine twice — once as an opportunity and again as the accepted quote it became. Turn it on only where opportunities do not become quotes.",
          },
        },
      },
    },
  },
  fr: {
    sop: {
      title: "Planification S&OP",
      search: {
        subtitle: "Plan tactique",
      },
      notifications: {
        categories: {
          planning: "Planification S&OP",
        },
      },
      units: {
        days: "{count} jours",
      },
      actions: {
        newPlan: "Nouveau plan",
        createPlan: "Créer le plan",
        calculate: "Calculer",
        approve: "Approuver",
        handTo: "Passer à {stage}",
        save: "Enregistrer",
        cancel: "Annuler",
        apply: "Appliquer",
        remove: "Retirer",
        archive: "Archiver",
        openCatalogue: "Ouvrir le catalogue",
        openWorkCenters: "Ouvrir les centres de travail",
        seedDemo: "Créer des données de démonstration",
      },
      pager: {
        previous: "Précédent",
        next: "Suivant",
        of: "Page {page} sur {pages}",
      },
      empty: {
        none: "Il n\u0027y a encore aucun plan S&OP",
        noneHint: "Créez le premier pour analyser la demande, la capacité et les stocks des prochains mois.",
        filtered: "Aucun résultat",
        filteredHint: "Essayez de retirer un filtre ou de chercher autre chose.",
      },
      status: {
        draft: "Brouillon",
        preparation: "Préparation",
        demand_review: "Revue de la demande",
        supply_review: "Revue des approvisionnements",
        capacity_review: "Revue de la capacité",
        scenario_review: "Revue des scénarios",
        financial_review: "Revue financière",
        executive_review: "Réunion de direction",
        approved: "Approuvé",
        closed: "Clôturé",
      },
      cycle: {
        title: "Cycle S&OP",
        moved: "Le plan passe à {stage}",
      },
      calc: {
        never: "Non calculé",
        stale: "Obsolète",
        running: "En cours de calcul",
        ready: "À jour",
        failed: "Échec du calcul",
        lastRun: "Dernier calcul : {when}",
        done: "Plan calculé en {ms} ms · {risks} risques",
        notice: {
          never: "Ce plan n\u0027a pas encore été calculé : les écrans restent vides tant qu\u0027il ne l\u0027est pas.",
          stale: "Quelque chose a changé depuis le dernier calcul. Les chiffres affichés sont les anciens.",
          running: "Le plan est en cours de calcul.",
          ready: "Les chiffres sont à jour.",
          failed: "Le dernier calcul a échoué. Vérifiez la préparation des données et réessayez.",
        },
      },
      granularity: {
        month: "Mois",
        week: "Semaine",
        quarter: "Trimestre",
      },
      fence: {
        frozen: "figé",
        firm: "ferme",
        flexible: "flexible",
      },
      plans: {
        title: "Plans",
        subtitle: "Les plans S&OP de l\u0027entreprise, et où en est chacun dans son cycle",
        detailTitle: "Plan S&OP",
        newTitle: "Nouveau plan S&OP",
        newSubtitle: "Ce qui est planifié, jusqu\u0027à quand, et selon quelles limites",
        versionLabel: "v{version}",
        created: "Plan {code} créé",
      },
      table: {
        code: "Code",
        name: "Plan",
        horizon: "Horizon",
        status: "Étape",
        calc: "Calcul",
        version: "Version",
      },
      tabs: {
        overview: "Résumé",
        demand: "Demande",
        supply: "Approvisionnement",
        capacity: "Capacité",
        inventory: "Stocks",
        procurement: "Achats",
        scenarios: "Scénarios",
        risks: "Risques",
        versions: "Plan approuvé",
        actual: "Suivi",
      },
      filters: {
        search: "Rechercher",
        searchPlaceholder: "Code ou nom du plan…",
        status: "Étape",
        allStatuses: "Tous",
        family: "Famille",
        allFamilies: "Toutes les familles",
        sourcing: "Origine",
        allSourcings: "Fabriqué et acheté",
        planned: "Dans le plan",
        allItems: "Tout le catalogue",
        state: "État",
        allStates: "Tous les états",
        risk: "Risque",
        allRisks: "Tous",
        severity: "Gravité",
        allSeverities: "Toutes",
        riskType: "Type",
        allTypes: "Tous les types",
      },
      kpi: {
        openPlans: "Plans ouverts",
        approvedPlans: "Plans approuvés",
        closedPlans: "Plans clôturés",
        needsCalc: "En attente de calcul",
        demand: "Demande prévue",
        confirmed: "Déjà confirmé",
        peakUtilisation: "Utilisation maximale",
        capacityGap: "Heures manquantes",
        stockouts: "Matières en rupture",
        service: "Niveau de service estimé",
        purchaseRisk: "Achats à risque",
        revenue: "Chiffre d\u0027affaires prévu",
        cost: "Coût prévu",
        margin: "Marge prévue",
        inventoryValue: "Valeur de stock maximale",
      },
      cockpit: {
        title: "Résumé S&OP",
        subtitle: "Où en est le plan, et ce qu\u0027il faut décider",
        pick: "Plan",
        openPlan: "Ouvrir le plan",
        decisions: "Décisions à prendre",
        decisionsHint: "D\u0027abord ce qui est déjà écrit, puis les risques critiques encore sans décision",
        noDecisions: "Rien en attente de décision",
        criticalRisks: "Risques critiques",
        criticalRisksHint: "Ceux qui ne se règlent pas d\u0027eux-mêmes",
        demandLine: "{confirmed} est déjà confirmé",
        capacityGap: "{hours} manquantes sur {centers} centres",
        capacityOk: "L\u0027usine y arrive",
        inventoryLine: "{below} sous le stock de sécurité",
        supplyLine: "sur {lines} lignes d\u0027achat",
        serviceLine: "Estimation du plan, non mesurée sur les livraisons",
        financeLine: "Coût {cost} · marge {margin}",
        riskWhy: "Risque critique en {period}",
        riskWhyNoPeriod: "Risque critique du plan",
        areas: {
          demand: "Demande",
          capacity: "Capacité",
          inventory: "Stocks",
          supply: "Approvisionnement",
          service: "Service",
          finance: "Finances",
        },
      },
      overview: {
        notCalculated: "Le plan n\u0027a pas encore été calculé",
        notCalculatedHint: "Lancez-le pour voir la demande, la capacité, les stocks et les risques.",
        criticalMonth: "{center} ne passe pas en {period}",
        criticalLine: "Il est à {utilisation} : il faut {required} et il y en a {available}.",
        bottlenecks: "Goulots d\u0027étranglement",
        bottlenecksHint: "Classés par dépassement, non par nombre d\u0027heures",
        noBottlenecks: "Aucun centre ne dépasse sa capacité",
        alerts: "Alertes",
        alertsHint: "Ce qui ne passe pas, avec ce qu\u0027on pourrait y faire",
        noRisks: "Aucun risque ouvert",
        optionHint: "« {option} » se décide sur cet écran",
      },
      charts: {
        demandVsCapacity: "Demande face à la capacité",
        demandVsCapacityHint: "Les barres sont les heures demandées par le plan ; la ligne, celles dont dispose l\u0027usine",
        requiredHours: "Heures nécessaires",
        availableHours: "Heures disponibles",
        sales: "Évolution des ventes",
        salesHint: "Ce qui est déjà commandé, et ce qui est prévisionnel",
        confirmed: "Commandes confirmées",
        forecast: "Prévision",
        stock: "Stock projeté",
        stockHint: "L\u0027état du magasin mois par mois",
        projectedStock: "Stock projeté",
      },
      readiness: {
        title: "Préparation des données",
        subtitle: "Ce que le plan n\u0027a pas pu lire. Un calcul sur des données incomplètes n\u0027échoue pas : il donne un résultat faux.",
        ready: "des données nécessaires",
        count: "{ready} sur {total}",
        feeds: "Ce qui arrive des autres modules",
        feedRows: "{count} enregistrements · dernier {when}",
        feedEmpty: "Rien pour l\u0027instant",
        checks: {
          routing: "Produits avec une gamme",
          bom: "Produits avec une nomenclature",
          leadTime: "Matières avec un délai",
          cost: "Articles avec un coût",
          price: "Produits avec un prix",
          family: "Articles avec une famille",
          stock: "Articles avec un stock connu",
          supplier: "Matières avec un fournisseur",
          calendar: "Centres avec un calendrier",
        },
      },
      feeds: {
        sales_history: "Historique des ventes",
        order_book: "Carnet de commandes",
        open_production: "Ordres de fabrication ouverts",
        open_purchase: "Commandes d\u0027achat ouvertes",
        production_actual: "Production réelle",
        purchase_actual: "Réceptions",
        stock_level: "Niveaux de stock",
        absence: "Absences approuvées",
        maintenance: "Maintenances prévues",
        asset_stop: "Machines à l\u0027arrêt",
      },
      quality: {
        itemsWithoutRouting: "Produits sans gamme",
        itemsWithoutBom: "Produits sans nomenclature",
        itemsWithoutLeadTime: "Matières sans délai",
        itemsWithoutCost: "Articles sans coût",
        itemsWithoutStock: "Articles sans stock connu",
        workCentersWithoutCalendar: "Centres sans calendrier",
      },
      demand: {
        item: "Article",
        total: "Total",
        consensusTotal: "Demande S&OP",
        showing: "Affichage",
        truncated: "Il y a plus d\u0027articles que d\u0027espace. Filtrez par famille pour les voir.",
        empty: "Il n\u0027y a rien à planifier",
        emptyHint: "Marquez des articles du catalogue comme planifiables pour qu\u0027ils apparaissent ici.",
        generate: "Générer une prévision",
        generated: "Prévision générée · {items} articles sur {items} avaient un historique",
        bulk: "Édition en lot",
        bulkTitle: "Édition en lot",
        bulkOperation: "Que faire",
        bulkValue: "Valeur",
        bulkField: "Sur quoi",
        bulkHint: "Cela s\u0027applique à ce qui est à l\u0027écran, et jamais à une période verrouillée.",
        bulkDone: "{changed} cellules modifiées",
        bulkDoneSkipped: "{changed} cellules modifiées · {skipped} verrouillées non touchées",
        fields: {
          consensus: "Demande consensuelle",
          confirmed: "Commandes confirmées",
          baseline: "Prévision",
          pipeline: "Opportunités CRM",
          adjustment: "Ajustement commercial",
          override: "Chiffre décidé",
        },
        hints: {
          consensus: "La somme de tout le reste, ou le chiffre décidé s\u0027il y en a un. C\u0027est ce que le reste du module planifie.",
          confirmed: "Provient des devis acceptés. Ne se saisit pas ici.",
          baseline: "Ce que dit l\u0027historique, ou ce que vous écrivez par-dessus.",
          pipeline: "Opportunités du CRM pondérées par leur probabilité. Ne se saisit pas ici.",
          adjustment: "Ce que le commerce sait et qui n\u0027apparaît dans aucun des chiffres ci-dessus.",
          override: "Il remplace la somme au lieu de s\u0027y ajouter. Vide signifie qu\u0027il n\u0027y en a pas.",
        },
        operations: {
          increase: "Augmenter d\u0027un pourcentage",
          set: "Fixer une valeur",
          distribute: "Répartir un total",
          clear: "Vider",
        },
      },
      sourcing: {
        make: "Fabriqué",
        buy: "Acheté",
      },
      itemKind: {
        finished: "Produit fini",
        component: "Composant",
        raw: "Matière première",
        service: "Service",
      },
      centerKind: {
        machine: "Machine",
        labour: "Personnes",
        mixed: "Mixte",
      },
      load: {
        idle: "Sans charge",
        normal: "Normal",
        warning: "Serré",
        over: "En surcharge",
        critical: "Critique",
      },
      stock: {
        stockout: "Rupture",
        below_safety: "Sous le stock de sécurité",
        low: "Juste",
        healthy: "Correct",
        excess: "Excédent",
      },
      supply: {
        showing: "Affichage",
        level: "niveau {level}",
        empty: "Il n\u0027y a rien à approvisionner",
        emptyHint: "Lancez-le avec de la demande et ceci montrera ce qu\u0027il faut fabriquer et acheter.",
        problems: "Problèmes dans les données",
        problemsHint: "Défauts d\u0027une nomenclature ou d\u0027une gamme, pas des risques du plan",
        fields: {
          start: "À lancer",
          planned: "Doit arriver",
          gross: "Besoin brut",
          independent: "Demande propre",
          dependent: "Demande de ses parents",
          scheduled: "Déjà en route",
          projected: "Stock résultant",
        },
        hints: {
          start: "La période où la décision doit être prise, délai déjà déduit. C\u0027est la ligne sur laquelle on agit.",
          planned: "La période où il doit être là.",
          gross: "Ce que consomme tout le plan : ce qui se vend et ce qui entre dans autre chose.",
          independent: "Ce qui est vendu directement.",
          dependent: "Ce qui part dans les produits qui l\u0027utilisent.",
          scheduled: "Ordres de fabrication et commandes d\u0027achat déjà ouverts.",
          projected: "L\u0027état du magasin à la fin de chaque période.",
        },
      },
      problems: {
        BOM_CYCLE: "Nomenclature circulaire",
        BOM_ITEM_MISSING: "Composant absent du catalogue",
        BOM_TOO_DEEP: "Nomenclature trop profonde",
        ROUTING_MISSING: "Produit sans gamme",
        WORK_CENTER_MISSING: "Gamme pointant vers un centre qui n\u0027existe plus",
      },
      capacity: {
        center: "Centre de travail",
        heatmap: "Charge par centre et par mois",
        heatmapHint: "Cliquez sur une cellule pour voir ce qui la charge",
        detail: "Centres de travail",
        detailHint: "Ce que demande le plan face à ce qui existe, sur tout l\u0027horizon",
        available: "Disponible",
        required: "Nécessaire",
        gap: "Écart",
        peak: "Maximum",
        utilisation: "Utilisation",
        calendar: "Calendrier",
        calendarBreakdown: "D\u0027où viennent les heures",
        downtime: "Maintenance et arrêts",
        absence: "Absences",
        scenarioHours: "Heures du scénario",
        whatLoadsIt: "Ce qui le charge",
        nothingLoads: "Rien ne le charge sur cette période",
        empty: "Il n\u0027y a aucun centre de travail",
        emptyHint: "Sans centres, il n\u0027y a pas de capacité à calculer : l\u0027usine ressort vide.",
      },
      inventory: {
        showing: "Affichage",
        worst: "Pire mois",
        safety: "sécurité {quantity}",
        value: "Valeur",
        valueChart: "Valeur du stock",
        valueChartHint: "Ce qu\u0027il faut financer, mois par mois",
        empty: "Il n\u0027y a pas de stock à projeter",
        emptyHint: "Lancez-le avec de la demande et un catalogue.",
        excess: "Excédents et obsolètes",
        excessHint: "Ce que le plan ne consommera pas",
        excessType: "Type",
        quantity: "Quantité",
        cover: "Couverture",
        horizons: "{count} fois l\u0027horizon",
        excessKind: {
          dead: "Sans mouvement",
          excess: "Excédent",
        },
        fields: {
          closing: "Stock final",
          demand: "Consommation",
          receipts: "Réceptions prévues",
          production: "Réapprovisionnement planifié",
          coverageDays: "Jours de couverture",
          value: "Valeur",
        },
      },
      procurement: {
        hint: "Trié par date limite de commande, non par montant : l\u0027urgent est ce qui n\u0027arrive plus à temps.",
        need: "À acheter",
        ofTotal: "sur {total} de besoin total",
        stock: "Stock",
        onOrder: "{quantity} déjà commandés",
        leadTime: "Délai",
        orderBy: "Commander avant le",
        risk: "Risque",
        cost: "Montant",
        noSupplier: "sans fournisseur",
        noLeadTime: "Sans délai",
        unplaceable: "{count} périodes qu\u0027on ne peut plus commander",
        spend: "Engagement d\u0027achat",
        spendHint: "Ce qu\u0027il faut engager chaque mois, non ce qui arrive",
        empty: "Il n\u0027y a rien à acheter",
        emptyHint: "Avec la demande et le stock actuels, le plan se couvre tout seul.",
        risks: {
          none: "Dans les temps",
          tight: "Juste",
          late: "En retard",
        },
      },
      scenarios: {
        base: "Plan de base",
        new: "Nouveau scénario",
        manage: "Scénarios",
        intro: "Un scénario est une question, pas une copie du plan : quelques leviers sur les mêmes données.",
        metric: "Indicateur",
        name: "Nom",
        description: "Description",
        template: "Point de départ",
        templateHint: "Remplit les leviers habituels de cette question. Ils restent modifiables.",
        levers: "Leviers",
        lever: "Levier",
        leverCount: "Leviers",
        addLever: "Ajouter un levier",
        noLevers: "Sans leviers : c\u0027est le plan tel quel",
        pickCenter: "Choisir un centre",
        pickItem: "Choisir un article",
        value: "Valeur",
        from: "À partir de",
        to: "Jusqu\u0027à",
        fromAny: "Tout l\u0027horizon",
        toAny: "Tout l\u0027horizon",
        active: "À l\u0027écran",
        recommended: "Recommandé",
        approved: "Approuvé",
        read: "Lire avec celui-ci",
        reading: "Le plan est lu à travers ce scénario",
        recommend: "Recommander",
        recommendedDone: "« {name} » devient le scénario recommandé",
        created: "Scénario créé. Calculez-le pour voir ses chiffres.",
        removeTitle: "Retirer le scénario",
        removeMessage: "« {name} » et ses chiffres disparaissent. Le plan lui-même n\u0027est pas touché.",
      },
      templates: {
        base: "Plan de base",
        demand_up: "La demande augmente",
        demand_down: "La demande baisse",
        extra_shift: "Un poste de plus",
        hire: "Embaucher",
        subcontract: "Sous-traiter",
        new_machine: "Une nouvelle machine",
        lead_time_up: "Les délais s\u0027allongent",
        machine_down: "Une machine s\u0027arrête",
        cost_up: "Les coûts augmentent",
      },
      levers: {
        everything: "tout",
        between: "de {from} à {to}",
        demand_pct: "Demande",
        demand_delta: "Demande, en unités",
        capacity_shifts: "Postes",
        capacity_resources: "Machines ou personnes",
        capacity_hours: "Heures supplémentaires",
        capacity_efficiency: "Efficacité du centre",
        capacity_subcontract: "Heures sous-traitables",
        capacity_offline: "Heures perdues",
        lead_time_days: "Délai de livraison",
        purchase_cost_pct: "Coût d\u0027achat",
        minimum_order: "Commande minimale",
        routing_efficiency: "Efficacité de la gamme",
        sourcing: "Fabriquer ou acheter",
        safety_stock: "Stock de sécurité",
        safety_stock_pct: "Stock de sécurité, en pourcentage",
        opening_stock: "Stock initial",
      },
      metrics: {
        revenue: "Chiffre d\u0027affaires",
        cost: "Coût",
        marginPct: "Marge",
        serviceLevelEstimate: "Niveau de service",
        peakUtilisation: "Charge maximale",
        capacityGapHours: "Heures manquantes",
        stockoutItems: "Matières en rupture",
        peakInventoryValue: "Investissement en stock",
        criticalRisks: "Risques critiques",
        good: {
          up: "plus c\u0027est haut, mieux c\u0027est",
          down: "plus c\u0027est bas, mieux c\u0027est",
        },
      },
      severity: {
        low: "Faible",
        medium: "Moyenne",
        high: "Élevée",
        critical: "Critique",
      },
      riskStatus: {
        open: "Ouvert",
        accepted: "Accepté",
        mitigating: "En cours de traitement",
        resolved: "Résolu",
        dismissed: "Écarté",
      },
      riskType: {
        capacity: "Capacité",
        stock: "Rupture de stock",
        material: "Matière",
        supplier: "Fournisseur",
        lead_time: "Délai",
        demand: "Demande",
        machine: "Machine",
        personnel: "Personnel",
        skill: "Compétences",
        quality: "Qualité",
        financial: "Financier",
        logistic: "Logistique",
        data: "Données",
      },
      risks: {
        options: "Ce qu\u0027on peut faire",
        status: "Statut du risque",
        critical: "Critiques",
        high: "Élevés",
        medium: "Moyens",
        open: "Ouverts",
        empty: "Aucun risque ouvert",
        emptyHint: "Le plan tient tel quel.",
        noSupplier: "fournisseur non défini",
        sentences: {
          capacity: "En {period} il est à {utilisation} : il faut {required} et il y a {available}. Il manque {gap}.",
          stock: "En {period} il finit à {closing} pour un minimum de {safety}. Il manque {shortfall}.",
          coverage: "Il ne reste que {days} jours de couverture, et le seuil est à {threshold}.",
          leadTime: "Il n\u0027arrive plus à temps pour {period} : {days} jours de délai chez {supplier}, et il en faut {quantity}.",
          supplier: "Pour {period} il fallait commander avant le {orderBy}, et {slack} jours ont passé. Fournisseur : {supplier}.",
          jump: "La prévision augmente de {jump} par rapport aux derniers mois : de {average} à {peak}.",
          bias: "La prévision dérive toujours dans le même sens : {bias}, et le seuil est à {threshold}.",
          data: "{count} articles sur {of} n\u0027ont pas cette donnée, le calcul ne les compte donc pas correctement.",
        },
      },
      options: {
        add_shift: "Ajouter un poste",
        subcontract: "Sous-traiter",
        hire: "Embaucher",
        move_earlier: "Avancer le travail",
        order_now: "Commander maintenant",
        bring_purchase_forward: "Avancer l\u0027achat",
        raise_safety_stock: "Relever le stock de sécurité",
        alternative_supplier: "Chercher un autre fournisseur",
        split_delivery: "Fractionner la livraison",
        confirm_forecast: "Confirmer la prévision",
        review_forecast_process: "Revoir la façon de faire la prévision",
        complete_routings: "Compléter les gammes",
        complete_boms: "Compléter les nomenclatures",
        complete_lead_times: "Compléter les délais",
        complete_costs: "Compléter les coûts",
        import_stock: "Charger le stock",
        complete_calendars: "Compléter les calendriers",
      },
      decisions: {
        title: "Décisions",
        subtitle: "Ce qui a été décidé, et pourquoi. C\u0027est ce qui explique le plan six mois plus tard.",
        record: "Consigner une décision",
        recordFor: "Consigner une décision",
        recorded: "Décision consignée",
        what: "Ce qui est décidé",
        whatPlaceholder: "Deuxième poste au montage, semaines 45-48",
        why: "Pourquoi",
        whyHint: "Obligatoire : une décision sans motif n\u0027explique rien dans six mois.",
        impact: "Ce que cela devrait changer",
        impactHint: "En mots : « +160 h ». La moitié de ces décisions n\u0027ont pas de chiffre.",
        hoursImpact: "+{hours} h",
        period: "Période",
        anyPeriod: "Tout le plan",
        status: "Statut",
        owner: "Responsable",
        when: "Quand",
        empty: "Aucune décision consignée pour l\u0027instant",
        emptyHint: "Chaque changement du plan qui mérite une explication tient ici en deux lignes.",
        statuses: {
          proposed: "Proposée",
          approved: "Approuvée",
          rejected: "Rejetée",
          done: "Faite",
        },
      },
      approve: {
        title: "Approuver le plan",
        message: "Une version est figée avec les chiffres actuels. Elle ne pourra pas être modifiée, et tout changement ultérieur sera une nouvelle version.",
        messageScenario: "Le scénario « {scenario} » est approuvé et une version est figée avec ses chiffres. Elle ne pourra pas être modifiée.",
        done: "Plan approuvé · version {version}",
      },
      versions: {
        version: "Version",
        scenario: "Scénario",
        approvedAt: "Approuvée",
        approvedBy: "Par",
        comment: "Commentaire",
        compare: "Comparer les versions",
        compareHint: "Ce qui a changé entre deux signatures",
        first: "Version précédente",
        second: "Version suivante",
        frozen: "Cette version est figée : elle se lit aujourd\u0027hui exactement comme le jour de sa signature.",
        risksAtApproval: "Risques ouverts à l\u0027approbation",
        empty: "Aucune version n\u0027a encore été approuvée",
        emptyHint: "Quand la direction signe le plan, la version est figée ici.",
      },
      actual: {
        against: "Comparer avec",
        hint: "Face à la version {version}, signée le {when} par {who}.",
        period: "Période",
        sales: "Ventes",
        production: "Production",
        hours: "Heures",
        purchases: "Achats",
        utilisation: "Utilisation",
        salesVariance: "Écart des ventes",
        productionVariance: "Écart de production",
        hoursVariance: "Écart d\u0027heures",
        unavailable: "Il n\u0027y a encore rien à comparer",
        unavailableHint: "Le suivi se fait face à une version signée, et ce plan n\u0027en a aucune.",
        tooEarly: "Aucune période n\u0027est encore passée",
        tooEarlyHint: "Seul ce qui s\u0027est déjà produit est comparé, jamais ce qui reste à venir.",
        feeds: "D\u0027où viennent les chiffres réels",
        feedsHint: "Ce qui n\u0027est pas installé affiche un tiret, jamais un zéro",
        feedOn: "Reçu",
        feedOff: "Non installé",
        feedNames: {
          sales: "Facturation",
          production: "Production",
          purchases: "Achats",
        },
      },
      items: {
        title: "Articles planifiables",
        subtitle: "Ce qui est planifié, comment, et de quoi c\u0027est fait",
        hint: "Tout le catalogue du magasin arrive. Ne marquez que ce qui est réellement planifié.",
        new: "Nouvel article",
        newHint: "Un produit ou une matière que le magasin ne détient pas",
        edit: "Modifier l\u0027article",
        item: "Article",
        code: "Code",
        name: "Nom",
        family: "Famille",
        familyHint: "Le niveau auquel on planifie et on lit. C\u0027est le mot de l\u0027entreprise, pas une liste de notre part.",
        unit: "Unité",
        kind: "Type",
        status: "Dans le plan",
        inPlan: "Planifié",
        outOfPlan: "Hors plan",
        sourcing: "Origine",
        sourcingHint: "S\u0027il est fabriqué, il charge les centres de travail. S\u0027il est acheté, il va au plan d\u0027achats.",
        components: "Structure",
        component: "Composant",
        operations: "{count} opérations",
        stock: "Stock",
        stockHint: "D\u0027où part la projection. Il arrive du magasin quand celui-ci le publie, ou se saisit ici.",
        noStock: "Stock inconnu",
        missing: "Ce qui manque",
        mirrored: "du magasin",
        mirroredHint: "Le code, le nom, l\u0027unité, le coût et le fournisseur appartiennent au magasin et se mettent à jour seuls. Le reste appartient à ce module.",
        fromRecipe: "La nomenclature vient de la recette {code} de l\u0027atelier. En la modifiant ici, elle devient la vôtre : la recette ne l\u0027écrasera plus. Un composant absent du catalogue garde son nom, sans lien.",
        identity: "Ce que c\u0027est",
        policy: "Comment il est planifié",
        policyHint: "La moitié qu\u0027aucun autre module de l\u0027ERP ne possède",
        leadTime: "Délai, en jours",
        leadTimeHint: "Du moment où l\u0027on décide à celui où on l\u0027a. Du fournisseur si acheté, de l\u0027atelier si fabriqué.",
        safetyStock: "Stock de sécurité",
        safetyStockHint: "Le plancher sous lequel la projection ne descend jamais.",
        lotSize: "Multiple de commande",
        lotSizeHint: "Les quantités sont arrondies à ce multiple. Zéro signifie aucun.",
        minimumOrder: "Commande minimale",
        yield: "Rendement",
        yieldHint: "Quelle part sort bonne. À 96 %, il faut en lancer plus qu\u0027il n\u0027en faut.",
        price: "Prix de vente",
        priceHint: "Ce à partir de quoi le chiffre d\u0027affaires prévu est calculé",
        cost: "Coût",
        supplier: "Fournisseur",
        bom: "Nomenclature de planification",
        bomHint: "Au niveau où l\u0027on planifie : une machine a un moteur et un châssis, pas quatre cents vis.",
        quantityPerUnit: "Quantité par unité",
        scrap: "Chute %",
        pickComponent: "Choisir un composant",
        addComponent: "Ajouter un composant",
        routing: "Gamme",
        routingHint: "Par quels centres il passe et combien de temps dans chacun. Sans cela, il n\u0027y a pas de capacité à calculer.",
        workCenter: "Centre",
        operation: "Opération",
        minutesPerUnit: "Minutes par unité",
        setupMinutes: "Réglage",
        pickCenter: "Choisir un centre",
        addOperation: "Ajouter une opération",
        totalMinutes: "{minutes} minutes par unité au total",
        plannedField: "Cet article fait partie du plan",
        plannedFieldHint: "Seul ce qui est marqué est calculé. Un catalogue entier n\u0027est pas un plan.",
        selected: "{count} sélectionnés",
        addToPlan: "Ajouter au plan",
        removeFromPlan: "Retirer du plan",
        plannedChanged: "{count} articles mis à jour",
        saved: "Article enregistré",
        searchPlaceholder: "Code, nom, famille…",
        empty: "Le catalogue de planification est vide",
        emptyHint: "Avec le magasin installé, il se remplit tout seul. Sans lui, il se saisit ici.",
        archiveTitle: "Archiver l\u0027article",
        archiveMessage: "« {name} » cesse d\u0027être planifié. Il n\u0027est pas supprimé : les plans signés le nomment encore.",
        flags: {
          routing: "Sans gamme",
          bom: "Sans nomenclature",
          leadTime: "Sans délai",
          family: "Sans famille",
        },
      },
      centers: {
        title: "Centres de travail",
        subtitle: "Où l\u0027on fabrique, et combien d\u0027heures il y a réellement",
        hint: "Huit heures de calendrier ne font pas huit heures de production : l\u0027efficacité et la disponibilité décident.",
        new: "Nouveau centre",
        edit: "Modifier le centre",
        center: "Centre",
        code: "Code",
        name: "Nom",
        kind: "Ce qui manque en premier",
        kindHint: "Une machine, des personnes, ou les deux. Cela décide de ce qu\u0027une absence lui retire.",
        allKinds: "Tous",
        skill: "Compétence",
        skillHint: "Un mot, celui de l\u0027entreprise : « soudure certifiée ». Il regroupe les centres pour qu\u0027un manque se lise comme un manque DE quelque chose.",
        calendar: "Calendrier",
        calendarLine: "{hours} h × {shifts} postes × {resources}",
        daysPerWeek: "{days} jours par semaine",
        hoursPerShift: "Heures par poste",
        shifts: "Postes",
        daysPerWeekField: "Jours par semaine",
        resources: "Combien en parallèle",
        resourcesHint: "Quatre CNC, ou six soudeurs. Cela multiplie les heures.",
        factors: "Efficacité et disponibilité",
        factorsHint: "Deux faits distincts venant de deux personnes différentes : quelle part d\u0027une heure machine est du travail, et combien de temps la machine est apte à tourner.",
        efficiency: "Efficacité %",
        availability: "Disponibilité %",
        maintenanceHours: "Heures par maintenance",
        maintenanceHoursHint: "Ce que coûte ici une maintenance préventive. La maintenance annonce l\u0027échéance ; combien d\u0027heures elle prend, seule l\u0027usine le sait.",
        money: "Coût de l\u0027heure",
        costPerHour: "Heure normale",
        overtimeCost: "Heure supplémentaire",
        subcontractCost: "Heure sous-traitée",
        subcontractHours: "Heures qu\u0027un sous-traitant peut absorber",
        subcontractHoursHint: "Zéro signifie que ce travail ne sort pas de l\u0027usine, ce qui est la réponse habituelle.",
        monthlyCapacity: "Capacité mensuelle",
        ofCalendar: "sur {hours} de calendrier",
        links: "À quoi il est relié",
        crewCount: "{count} personnes",
        assetCount: "{count} machines",
        subcontract: "{hours} h à l\u0027extérieur",
        preview: "Avec ces chiffres, un mois normal donne {hours}.",
        saved: "Centre enregistré",
        searchPlaceholder: "Code, nom, compétence…",
        empty: "Il n\u0027y a encore aucun centre de travail",
        emptyHint: "Ils appartiennent à ce module : nulle part ailleurs dans l\u0027ERP on ne sait combien d\u0027heures a l\u0027usine.",
        archiveTitle: "Archiver le centre",
        archiveMessage: "« {name} » est retiré de toutes les gammes qui le nommaient. Sinon ces heures disparaîtraient du plan sans prévenir.",
      },
      form: {
        name: "Nom du plan",
        namePlaceholder: "S&OP 2027",
        nameRequired: "Le plan a besoin d\u0027un nom",
        description: "Description",
        site: "Usine",
        siteHint: "Le site de l\u0027entreprise concerné par ce plan. Sur une instance mono-site, il peut rester vide.",
        sitePlaceholder: "Usine 1",
        siteLinked: "Un site de l\u0027entreprise",
        siteUnlink: "Saisir le nom à la main",
        from: "Commence en",
        fromRequired: "Un mois de départ est nécessaire",
        months: "Combien de mois",
        monthsHint: "Douze est la norme en fabrication. Plus, quand les délais sont si longs qu\u0027un an ne suffit pas pour décider.",
        monthsCount: "{count} mois",
        granularity: "Planifié par",
        granularityHint: "Des mois presque toujours : ceci est le plan tactique, pas l\u0027ordonnancement de l\u0027atelier.",
        currency: "Devise",
        range: "De {from} à {to}",
        capacityWarn: "Avertissement de capacité %",
        capacityWarnHint: "En dessous, le mois est normal. C\u0027est un avertissement, pas une limite : rien n\u0027est refusé.",
        capacityOver: "Surcharge %",
        capacityCritical: "Critique %",
        frozenWeeks: "Semaines figées",
        frozenWeeksHint: "Les mois proches, où la matière est déjà achetée et où changer coûte de l\u0027argent.",
        firmMonths: "Mois fermes",
        usePipeline: "Compter les opportunités du CRM",
        usePipelineHint: "Désactivé volontairement : une entreprise qui devise tout compterait la même machine deux fois, comme opportunité et comme devis accepté.",
      },
      wizard: {
        step1: "1 · De quel plan il s\u0027agit",
        step1Hint: "Le nom sous lequel on en parlera en réunion",
        step2: "2 · Jusqu\u0027où il regarde",
        step2Hint: "L\u0027horizon et la taille du seau",
        step3: "3 · Selon quoi il est jugé",
        step3Hint: "Ils sont copiés dans le plan : une version approuvée conserve les limites sous lesquelles elle a été signée.",
        step4: "4 · Y a-t-il quelque chose à planifier",
        step4Hint: "Un plan sur un catalogue vide calcule une usine vide, et cela ressemble beaucoup à une réponse.",
        plannedItems: "Articles dans le plan",
        workCenters: "Centres de travail",
        missingMasters: "Sans articles planifiables ni centres de travail, le calcul ne dira rien d\u0027utile.",
      },
      demo: {
        created: "Usine de démonstration créée : {items} articles et {centers} centres",
        labels: {
          site: "Usine 1",
          planDescription: "Plan de démonstration : fabrication industrielle de machines",
          extraShift: "Poste supplémentaire au montage",
          extraShiftHint: "Un deuxième poste au montage pendant le mois critique",
          subcontract: "Sous-traiter le montage",
          subcontractHint: "Externaliser une partie du montage, avec ce que cela coûte",
          demandUp: "Demande +15 %",
          demandUpHint: "Ce qui se passe si l\u0027année est meilleure que prévu",
        },
      },
      dashboard: {
        peak: "Charge maximale prévue",
        peakHelp: "L\u0027utilisation du centre le plus chargé, dans son pire mois, sur tous les plans ouverts.",
        risks: "Risques critiques",
        risksHelp: "Combien de risques critiques restent ouverts sur les plans non clôturés.",
        gap: "Heures manquantes",
        gapHelp: "Le total des heures que les plans ouverts demandent au-delà de ce que l\u0027usine a.",
        revenue: "Chiffre d\u0027affaires planifié",
        revenueHelp: "Le total des prévisions des plans ouverts, aux prix du catalogue.",
        riskList: "Les risques critiques",
        riskListHelp: "Ce qui est à risque, dans quel plan et quel mois. Étirez la carte et il en tient plus.",
        stale: "Plans à recalculer",
        staleHelp: "Les plans dont les chiffres ne correspondent plus aux données sous-jacentes.",
      },
    },
    permissions: {
      sop: {
        read: "Voir les plans S&OP",
        demand: "Travailler la demande : prévision, ajustements et consensus",
        supply: "Travailler l\u0027approvisionnement : catalogue, gammes, centres de travail et calcul",
        scenarios: "Créer et comparer des scénarios",
        approve: "Approuver le plan et consigner des décisions",
        costs: "Voir ce que vaut le plan : chiffre d\u0027affaires, coût et marge",
        admin: "Ouvrir des plans, les configurer et les archiver",
      },
    },
    audit: {
      actions: {
        sop: {
          plan_created: "Plan S&OP créé",
          plan_updated: "Plan S&OP modifié",
          plan_stage_changed: "Plan passé à une autre étape du cycle",
          plan_calculated: "Plan S&OP calculé",
          plan_approved: "Plan S&OP approuvé",
          plan_closed: "Plan S&OP clôturé",
          plan_archived: "Plan S&OP archivé",
          plan_restored: "Plan S&OP remis dans les listes",
          plan_commented: "Commentaire sur le plan",
          plan_comment_removed: "Commentaire supprimé du plan",
          demand_edited: "Cellule de demande modifiée",
          demand_bulk_edited: "Demande modifiée en lot",
          demand_locked: "Périodes de demande verrouillées",
          baseline_generated: "Prévision générée depuis l\u0027historique",
          item_created: "Article de planification créé",
          item_updated: "Article de planification modifié",
          item_archived: "Article de planification archivé",
          item_restored: "Article remis au catalogue",
          items_planned: "Articles ajoutés au plan ou retirés",
          item_stock_set: "Stock initial corrigé",
          work_center_created: "Centre de travail créé",
          work_center_updated: "Centre de travail modifié",
          work_center_archived: "Centre de travail archivé",
          work_center_restored: "Centre de travail remis au catalogue",
          scenario_created: "Scénario créé",
          scenario_updated: "Scénario modifié",
          scenario_archived: "Scénario retiré",
          scenario_activated: "Scénario affiché",
          scenario_recommended: "Scénario recommandé",
          risk_raised: "Risque saisi manuellement",
          risk_updated: "Risque mis à jour",
          decision_recorded: "Décision S&OP consignée",
          decision_updated: "Décision S&OP mise à jour",
          demo_seeded: "Données de démonstration créées",
        },
      },
    },
    assistant: {
      openers: {
        sop: {
          risks: "Quels risques avons-nous en novembre ?",
          bottleneck: "Où est le goulot d\u0027étranglement du plan ?",
          scenarios: "Compare les scénarios du plan actuel",
        },
      },
    },
    settings: {
      ns: {
        sop: {
          title: "Planification S&OP",
          subtitle: "Comment cette entreprise planifie : horizon, seuils et ce qui compte comme demande",
          defaultHorizonMonths: {
            label: "Nombre de mois qu\u0027un nouveau plan anticipe",
            help: "Douze est la réponse habituelle en fabrication. Dix-huit ou vingt-quatre si les délais sont si longs qu\u0027un an ne suffit pas pour décider.",
          },
          defaultGranularity: {
            label: "Le seau d\u0027un nouveau plan",
            help: "Des mois, presque toujours : ceci est le plan tactique, pas l\u0027ordonnancement. Des semaines seulement si l\u0027horizon est court et les décisions aussi fines.",
          },
          currency: {
            label: "La devise dans laquelle les plans sont valorisés",
            help: "Code ISO. C\u0027est ce en quoi le chiffre d\u0027affaires, le coût et la marge d\u0027un plan sont exprimés.",
          },
          capacityWarnPct: {
            label: "Utilisation à partir de laquelle surveiller un centre",
            help: "En dessous, la période est normale. C\u0027est un avertissement, pas une limite : rien n\u0027est refusé.",
          },
          capacityOverPct: {
            label: "Utilisation au-delà de laquelle un centre ne passe plus",
            help: "Cent signifie que le plan demande plus d\u0027heures que l\u0027usine n\u0027en a. Certaines usines placent leur repère au-dessus, car leurs temps standards sont prudents.",
          },
          capacityCriticalPct: {
            label: "Utilisation à partir de laquelle la période est un risque critique",
            help: "Au-delà, le plan lève un risque critique et le bus le transporte. Placez-le là où les heures supplémentaires et un peu de sous-traitance ne suffisent plus.",
          },
          coverageWarnDays: {
            label: "Jours de couverture en dessous desquels alerter",
            help: "Mesuré face à la demande qui SUIT le solde, jamais face au passé. Une pièce sans demande à venir n\u0027a ni couverture ni alerte.",
          },
          forecastBiasWarnPct: {
            label: "Biais de prévision qui devient un problème",
            help: "Le biais est l\u0027erreur moyenne SIGNÉE : une prévision fausse de 15 % dans le même sens mois après mois est un processus cassé, pas de la malchance.",
          },
          serviceTargetPct: {
            label: "Le niveau de service visé par l\u0027entreprise",
            help: "Ce à quoi le plan est comparé. Le chiffre produit est une ESTIMATION issue du plan lui-même : la mesure vient des livraisons, que ce module ne voit pas.",
          },
          frozenWeeks: {
            label: "Semaines d\u0027horizon figé",
            help: "Les périodes proches, où la matière est achetée et où changer coûte de l\u0027argent. Cela colore la grille ; verrouiller une cellule reste une décision humaine.",
          },
          firmMonths: {
            label: "Mois d\u0027horizon ferme après l\u0027horizon figé",
            help: "Ici les changements sont encadrés, pas impossibles. Tout ce qui suit est flexible.",
          },
          usePipeline: {
            label: "Compter les opportunités du CRM dans la demande",
            help: "Désactivé par défaut, volontairement : une entreprise qui devise tout compterait la même machine deux fois — comme opportunité, puis comme devis accepté. Ne l\u0027activez que si les opportunités ne deviennent pas des devis.",
          },
        },
      },
    },
  },
}

// The assistant cards (sop.assistant.*), from their own file. With the
// extension: the shell's i18n guard imports this file with plain Node.
import assistantMessages from './assistantMessages.js'

for (const [locale, block] of Object.entries(assistantMessages)) {
  messages[locale].sop.assistant = { ...messages[locale].sop.assistant, ...block }
}

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'

Object.assign(messages, withToolLabels(messages))
